import { bayer, hex } from "../shared/color";
import { DEEP, HOT, ORANGE, WHITE, YELLOW, drawBeam, paintCellPart, type BeamFx } from "./show";
import { drawOrdnance, launchCoil, markMeteor, stepOrdnance, throwCrackle, type Bolt, type Coil, type MeteorRock, type OrdnanceHost } from "./ordnance";
import { Fire, Letters, Particles } from "./entities";
import { drawStick, handOf, muzzleOf, nearHand, packPoint, peekNearHand, syncStick, type Hold, type Pose } from "./stick";
import { spriteRot, type Sprite } from "./art";
import { castPose, PixBuf } from "./rig";
import { drawRot } from "./rotsprite";
import { slabAlong, Slabs, segBox, type Slab } from "./slabs";
import { b64ToBytes, readOps } from "./terrain";
import type { AudioBus } from "./audio";
import { World } from "./world";
import { MAGNET_SLOW, PageFight, RAIL_DMG, minionBlastDmg, minionShotDmg, type FightHost } from "./challenge";
import { DrillRig } from "./drill";
import { heroFx } from "./skins";
import {
  AIR_ACCEL, AIR_DECEL, BACKGROUND, BODY_H, BODY_HALF, BOMBLET_R, CELL, COYOTE, DT, FLIP_VY, FREE_WEAPON_COUNT, GRAVITY,
  GRENADE_CD, GRENADE_R, GROUND_ACCEL, GROUND_DECEL, JET_ACCEL, JET_DECEL, JET_IGNITE, JET_MAX_RISE, JET_SPEED,
  JET_THRUST, JUMP_BUFFER, JUMP_VY, PLAYER_BLAST_DMG, PLAYER_BULLET_DMG, ROCKET_R, RUN, SNAP_DOWN, SNAP_UP, SOLID, WEAPONS,
} from "../shared/defs";
import type { Level, Pad, WeaponDef } from "../shared/types";

interface Bullet { x: number; y: number; vx: number; vy: number; life: number; pierce: number; dmg: number; dist: number; range: number; weapon: string; heavy: boolean; owner: number; ghost: boolean; guide?: boolean; volley?: number; stand?: number }
interface Rocket { x: number; y: number; a: number; speed: number; t: number; range: number; dist: number; weapon: string; ghost: boolean; wobble: number }
interface Nade { x: number; y: number; vx: number; vy: number; a: number; av: number; fuse: number; grounded: boolean; weapon: string; ghost: boolean }
interface Mirv { x: number; y: number; vx: number; vy: number; a: number; state: number; t: number; ct: number; bounces: number; ghost: boolean }
interface Chute { x: number; y: number; vx: number; vy: number; t: number }
interface Bomblet { x: number; y: number; vx: number; vy: number; a: number; av: number; fuse: number; t: number; bounced: boolean; ghost: boolean }
interface Pop { x: number; y: number; t: number; s: number }
interface Drone { x: number; y: number; vx: number; vy: number; a: number; battery: number; state: number; rotor: number; gun: number; firing: boolean; cd: number; beep: number; t: number; face: number; tilt: number; tiltV: number; age: number; gunA: number; barrel: number; spin: number; kick: number }
interface Beam { x0: number; y0: number; x1: number; y1: number; life: number; max: number }
interface Ring { x: number; y: number; r0: number; r1: number; life: number; max: number; thick: number; color: number }
interface Flash { x: number; y: number; r: number; life: number; max: number }
interface Heat { cells: Int32Array; ring: Uint8Array; t: number; dur: number; hot: number; acc: number }
interface Glow { x: number; y: number; r: number; k: number; life: number; max: number; color?: number }
interface Blaze { x: number; y: number; life: number; max: number; acc: number; power: number }

/** Original heat ramp CJ: white, hot, yellow, gold, orange, deep orange, red, then the charred tail. */
const HEAT_COLORS = ["#ffffff", "#fff3a6", "#ffe26b", "#ffc23d", "#ff8a2a", "#f2561d", "#c9281f", "#8a1f17", "#4f1a13", "#2a120e"];
const HEAT_PACKED = HEAT_COLORS.map((color) => hex(color));

export interface Remote {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  aim: number;
  facing: number;
  grounded: boolean;
  jet: number;
  tumble: number;
  weapon: number;
  hp: number;
  alive: boolean;
  name: string;
  tint: number;
  skin: string;
  drone: { x: number; y: number; a: number; rotor: number } | null;
}

/** Share of the page-damage scale one railgun shot may break loose as whole blocks. */
const RAIL_BREAK_SHARE = 0.01;
/** Share of the page-text scale one railgun shot may knock loose. */
const RAIL_LETTER_SHARE = 0.025;

/** Live meteors at once; older spent ones are dropped first. */
const METEOR_MAX = 4;

export class Sim {
  world: World;
  letters: Letters;
  chunks: Slabs;
  parts: Particles;
  fire: Fire;
  x: number;
  y: number;
  vx = 0;
  vy = 0;
  grounded = false;
  facing = 1;
  aim = 0;
  coyote = 0;
  buffer = 0;
  jumps = 1;
  dropT = 0;
  dropFrom = 0;
  jumpHeld = false;
  flipT = -1;
  tumble = 0;
  tumbleV = 0;
  airTime = 0;
  groundT = 0;
  jetOn = false;
  jetK = 0;
  packT = -1;
  stowT = -1;
  holdT = 0;
  thrusting = false;
  phase = 0;
  weapon = 0;
  cd = 0;
  /** Seconds left in a hit-stop freeze: drill, Punchline, or a Magnus clump. Owner only. */
  hitStop = 0;
  /** Staff cast pose clock (Meteor), -1 when idle. */
  castT = -1;
  /** Staff recover pose clock, started when the meteor lands. */
  recoverT = -1;
  /** Staff gem flare, 0..1. */
  gemFlash = 0;
  /** Red screen-edge tint requested this frame by a summoning meteor. */
  edgeTint = 0;
  private edgeShown = 0;
  drill: DrillRig;
  grenadeCd = 0;
  /** G landed while the glove was out. Thrown once the glove is idle. */
  private grenadeQueued = false;
  throwT = -1;
  thrown = false;
  pumpT = -1;
  charge = -1;
  flaming = false;
  shots = 0;
  private shotVolley = 0;
  explosions = 0;
  blocksBroken = 0;
  flameBurst = false;
  private burnAcc = 0;
  private burnDmg = new Map<number, number>();
  hp = 100;
  alive = true;
  shield = 0;
  skin = "stickman";
  camX = 0;
  camY = 0;
  shake = 0;
  trauma = 0;
  viewW = 800;
  viewH = 600;
  bullets: Bullet[] = [];
  rockets: Rocket[] = [];
  nades: Nade[] = [];
  mirvs: Mirv[] = [];
  chutes: Chute[] = [];
  bomblets: Bomblet[] = [];
  coils: Coil[] = [];
  bolts: Bolt[] = [];
  meteors: MeteorRock[] = [];
  pops: Pop[] = [];
  beams: Beam[] = [];
  rings: Ring[] = [];
  flashes: Flash[] = [];
  heats: Heat[] = [];
  lights: Glow[] = [];
  blazes: Blaze[] = [];
  screenFlash = 0;
  screenTint = "255,246,222";
  /** Red wash after a Journey hit. Challenge never sets this. */
  pain = 0;
  /** Seconds of sprite blink left after a Journey hit. */
  hurtBlink = 0;
  fxBuf: PixBuf | null = null;
  fxCanvas: HTMLCanvasElement | null = null;
  fxImage: ImageData | null = null;
  heatBuf: PixBuf | null = null;
  heatCanvas: HTMLCanvasElement | null = null;
  heatImage: ImageData | null = null;
  shadeBuf: PixBuf | null = null;
  shadeCanvas: HTMLCanvasElement | null = null;
  shadeImage: ImageData | null = null;
  drone: Drone | null = null;
  droneCd = 0;
  lookX = 0;
  lookY = 0;
  remotes: Remote[] = [];
  banned = new Set<string>();
  online = false;
  id = 1;
  t = 0;
  recoil = 0;
  recoilV = 0;
  kickRotVis = 0;
  held: Hold | null = null;
  seenOp = 0;
  fight: PageFight;
  /** Journey deaths stay down. Challenge and Sandbox still respawn. */
  downed = false;
  /** Drawn in world space, under the stickman. Journey's portal uses this. */
  worldDraw: ((ctx: CanvasRenderingContext2D) => void) | null = null;
  /** World-space pickup. The HUD turns this into a flight toward the coin counter. */
  onCoin: ((value: number, x: number, y: number, gem: boolean) => void) | null = null;
  /** Test harness. Null during play. */
  script: Partial<Pad> | null = null;
  private scriptFire = false;
  intro = 0;
  camTop = -40;
  private posed: Pose | null = null;
  private spawnX: number;
  private spawnY: number;
  private audio: AudioBus | null;
  private onShot: ((kind: string, payload: unknown) => void) | null;

  constructor(level: Level, z: number, audio: AudioBus | null, spawn?: { x: number; y: number }, onShot?: (kind: string, payload: unknown) => void, online = false, id = 1) {
    this.world = new World(level, z);
    this.letters = new Letters(level.glyphs, this.world);
    this.chunks = new Slabs(this.world, this.letters);
    this.parts = new Particles();
    const sim = this;
    this.fight = new PageFight({
      get x() { return sim.x; },
      get y() { return sim.y; },
      get vx() { return sim.vx; },
      get vy() { return sim.vy; },
      get aim() { return sim.aim; },
      get lookX() { return sim.lookX; },
      get lookY() { return sim.lookY; },
      get facing() { return sim.facing; },
      get flaming() { return sim.flaming; },
      get online() { return sim.online; },
      world: this.world,
      letters: this.letters,
      chunks: this.chunks,
      parts: this.parts,
      destruction: () => sim.destruction(),
      knock: (ix, iy) => sim.impulse(ix, iy),
      sfx: (id, pitch) => sim.sfx(id, pitch ?? 1),
      sting: (amount) => sim.bite(amount),
      heal: (amount) => sim.heal(amount),
      onPickup: (value, x, y, gem) => sim.onCoin?.(value, x, y, gem),
      shake: (n) => { sim.trauma = Math.min(1, sim.trauma + n); },
      markShot: () => { sim.shots++; },
      freeze: (seconds) => { sim.hitStop = Math.max(sim.hitStop, seconds); },
      bloom: (x, y, radius, weight) => {
        const rim = this.world.rim(7);
        this.heatRim(rim, 1.1, 1.85);
        this.flashScreen(0.26 + weight * 0.36, "214, 228, 236");
        this.lights.push({
          x, y, r: radius * (1.7 + weight * 0.7), k: 0.4 + weight * 0.22,
          life: 0.9, max: 0.9, color: hex("#ff2d42"),
        });
        this.lights.push({
          x, y, r: radius * (0.55 + weight * 0.2), k: 0.55,
          life: 0.14, max: 0.14, color: hex("#f4fbff"),
        });
        const sparks = Math.round(22 + weight * 36);
        for (let i = 0; i < sparks; i++) {
          const ang = Math.random() * Math.PI * 2;
          const speed = (90 + Math.random() * 420) * (0.7 + weight * 0.4);
          const life = 0.5 + Math.random() * 0.48;
          this.parts.spawn({
            x, y,
            vx: Math.cos(ang) * speed,
            vy: Math.sin(ang) * speed - 24,
            life, max: life, size: 1.3,
            color: i % 3 === 0 ? "#f4fbff" : i % 3 === 1 ? "#ff4458" : "#d5dde6",
            grav: 280, rubble: false, glow: true, kind: "ion",
          });
        }
        const glints = Math.round(10 + weight * 12);
        for (let i = 0; i < glints; i++) {
          const ang = Math.random() * Math.PI * 2;
          const speed = 40 + Math.random() * 150;
          const life = 0.62 + Math.random() * 0.38;
          this.parts.spawn({
            x: x + Math.cos(ang) * radius * 0.45,
            y: y + Math.sin(ang) * radius * 0.45,
            vx: Math.cos(ang) * speed,
            vy: Math.sin(ang) * speed - 16,
            life, max: life, size: 1.5 + weight * 0.35,
            color: i % 2 ? "#e8eef6" : "#ffd0d4",
            grav: 60, rubble: false, glow: true, kind: "spark",
          });
        }
      },
      hand: () => nearHand(sim.aimPose(), sim.held),
      peekHand: () => peekNearHand("local"),
      aimAt: () => ({ x: sim.lookX, y: sim.lookY }),
      flash: (amount) => sim.flashScreen(amount, "255,246,222"),
      glow: (x, y, r, k, life) => { sim.lights.push({ x, y, r, k, life, max: life }); },
    } satisfies FightHost);
    this.drill = new DrillRig({
      world: this.world,
      letters: this.letters,
      chunks: this.chunks,
      parts: this.parts,
      fling: (index, vx, vy, av, burn) => this.fling(index, vx, vy, av, burn),
      bite: (x, y, r, dmg, cap, token, now, every) => this.fight.drillBite(x, y, r, dmg, cap, token, now, every),
      blast: (x, y, r, dmg) => this.fight.hurt(x, y, r, dmg),
      cache: (index) => this.fight.isCache(index),
      spoil: (x, y) => this.fight.drillSpoil(x, y),
      sfx: (id) => this.sfx(id),
      rumble: (n) => { this.trauma = Math.min(1, Math.max(this.trauma, n)); },
      punch: (seconds) => { this.hitStop = Math.max(this.hitStop, seconds); },
      knock: (x, y, reach, power, lift) => { this.selfBlast(x, y, reach, power, lift); },
      players: (x, y, radius, dmg) => this.drillPlayers(x, y, radius, dmg),
      flash: (amount, tint) => this.flashScreen(amount, tint),
      chips: (x, y) => {
        const radius = ROCKET_R * 1.3;
        this.chunks.spall(x, y, radius, Math.round((7 + Math.random() * 4) * 1.3), 5, 12);
      },
      spectacle: (x, y) => this.drillSpectacle(x, y),
      onBoom: () => { this.explosions++; },
    });
    this.world.breaker = (index, vx, vy, av) => {
      const block = this.world.blocks[index];
      this.fight.noteBreak();
      const boss = block ? this.fight.prepare(index, block) : false;
      const before = this.chunks.list.length;
      const out = this.chunks.detach(index, vx, vy, av, false);
      if (boss && block) {
        const born = this.chunks.list.splice(before);
        let big: Slab | null = null;
        for (const slab of born) if (!big || slab.bw * slab.bh > big.bw * big.bh) big = slab;
        this.fight.spawnBoss(big, block.x + block.w / 2, block.y + block.h / 2, block.w, block.h);
        for (const slab of born) if (slab !== big) this.chunks.list.push(slab);
      }
      for (let i = 0; i < out.crumbs.length && i < 900; i += 3) {
        this.parts.chip(out.crumbs[i], out.crumbs[i + 1], vx * 0.5 + (Math.random() - 0.5) * 120, vy * 0.5 - (20 + Math.random() * 100), out.crumbs[i + 2]);
      }
      if (out.chunks.length && block) {
        this.blocksBroken++;
        this.sfx("blockBreak");
        this.trauma = Math.min(1, this.trauma + Math.min(0.08, Math.sqrt(Math.max(1, block.w * block.h)) / 3000));
      }
    };
    this.fire = new Fire();
    this.audio = audio;
    this.onShot = onShot ?? null;
    this.online = online;
    this.id = id;
    const spot = spawn ?? (online ? this.spawnPoint() : this.findSpawn());
    this.spawnX = spot.x;
    this.spawnY = spot.y;
    this.x = spot.x;
    this.y = spot.y - 84;
    this.lookX = this.x + 220;
    this.lookY = this.y - 40;
    this.snapCam(this.x, this.spawnY - 100);
    this.commitText();
  }

  /** Highest clear ledge, biased toward 35% of the page width. */
  private findSpawn() {
    const world = this.world;
    let best = { x: world.W * 0.3, y: 200, score: -1e9 };
    const limit = Math.min(world.H, 520);
    for (let x = world.W * 0.18; x < world.W * 0.82; x += 12) {
      const y = world.findSurface(x - 8, x + 8, 30, limit);
      if (Number.isNaN(y)) continue;
      const score = -y * 0.5 - Math.abs(x - world.W * 0.35) * 0.3;
      if (score > best.score) best = { x, y, score };
    }
    return best;
  }

  /** Spread online players across ledges when the level has no baked spawn list. */
  private spawnPoint() {
    const world = this.world;
    let best = { x: world.W * 0.5, y: 200, score: -1e9 };
    const limit = Math.min(world.H, 1100);
    const id = this.id || 1;
    for (let i = 0; i < 48; i++) {
      const x = world.W * (0.06 + (((id * 97 + i * 53) % 1000) / 1000) * 0.88);
      const y = world.findSurface(x - 8, x + 8, 30, limit);
      if (Number.isNaN(y)) continue;
      let near = 1e9;
      for (const remote of this.remotes) {
        if (remote.alive) near = Math.min(near, Math.hypot(remote.x - x, remote.y - y));
      }
      const score = Math.min(near, 900) - y * 0.15 + ((id * 13 + i * 17) % 120);
      if (score > best.score) best = { x, y, score };
    }
    return best;
  }

  setView(w: number, h: number) {
    this.viewW = w;
    this.viewH = h;
  }

  get def(): WeaponDef {
    return WEAPONS[this.weapon] ?? WEAPONS[0];
  }

  destruction(): number {
    const letters = this.letters.x.length;
    const popped = this.letters.popped;
    const cells = this.world.totalCells;
    const a = 0.5 * Math.min(1, popped / Math.min(0.6 * Math.max(1, letters), 2400));
    const b = 0.5 * Math.min(1, this.world.removedCells / Math.min(0.3 * cells, 120000));
    return a + b;
  }

  respawn() {
    this.x = this.spawnX;
    this.y = this.spawnY - 84;
    this.vx = 0;
    this.vy = 0;
    this.hp = 100;
    this.alive = true;
    this.grounded = false;
    this.jetOn = false;
    this.jetK = 0;
    this.shield = 1.5;
    this.audio?.play("spawn");
  }

  private sfx(id: string, pitch = 1) {
    this.audio?.play(id, pitch);
  }

  private ordnanceHost(): OrdnanceHost {
    return {
      world: this.world,
      parts: this.parts,
      letters: this.letters,
      fight: this.fight,
      slabs: this.chunks,
      alive: this.alive,
      px: this.x,
      py: this.y,
      camX: this.camX,
      camY: this.camY,
      viewW: this.viewW,
      viewH: this.viewH,
      sfx: (id, pitch) => this.sfx(id, pitch),
      fling: (index, vx, vy, av, burn) => this.fling(index, vx, vy, av, burn),
      knock: (x, y, reach, power, lift) => { this.selfBlast(x, y, reach, power, lift); },
      trauma: (n) => { this.trauma = Math.min(1, this.trauma + n); },
      heat: (rim, dur, hot) => this.heatRim(rim, dur, hot),
      blast: () => { this.explosions++; },
      flashScreen: (amount, color) => this.flashScreen(amount, color),
      ring: (x, y, r0, r1, life, thick, color) => { this.rings.push({ x, y, r0, r1, life, max: life, thick, color }); },
      flash: (x, y, r, life) => { this.flashes.push({ x, y, r, life, max: life }); },
      light: (x, y, r, k, life, color) => { this.lights.push({ x, y, r, k, life, max: life, color }); },
      spall: (x, y, radius, count, minR, maxR) => this.chunks.spall(x, y, radius, count, minR, maxR),
      freeze: (seconds) => { this.hitStop = Math.max(this.hitStop, seconds); },
      landed: () => { this.recoverT = 0; this.gemFlash = 1; if (this.posed) this.posed.ready = false; },
      edge: (k) => { this.edgeTint = Math.max(this.edgeTint, k); },
    };
  }

  step(dt: number, pad: Pad) {
    if (this.script) {
      const over = this.script;
      const next = { ...pad };
      for (const key of Object.keys(over) as (keyof Pad)[]) {
        const value = over[key];
        if (value !== undefined) next[key] = value as never;
      }
      const fire = next.fire;
      next.firePressed = over.firePressed ?? (fire && !this.scriptFire);
      this.scriptFire = fire;
      pad = next;
    } else this.scriptFire = pad.fire;
    if (this.posed) this.posed.ready = false;
    this.t += dt;
    this.intro += dt;
    if (this.hitStop > 0) {
      if (pad.altPressed) this.grenadeQueued = true;
      this.hitStop = Math.max(0, this.hitStop - dt);
      this.drill.ease(dt);
      this.parts.step(dt, this.world, this.fire);
      this.camera(dt);
      this.audio?.loop("drillGrind", false);
      return;
    }
    if (this.castT >= 0) {
      this.castT += dt;
      if (this.castT > 0.5) this.castT = -1;
    }
    if (this.recoverT >= 0) {
      this.recoverT += dt;
      if (this.recoverT > 0.55) this.recoverT = -1;
    }
    this.gemFlash = Math.max(0, this.gemFlash - dt * 2.6);
    this.recoilV += (-this.recoil * 420 - this.recoilV * 30) * dt;
    this.recoil += this.recoilV * dt;
    this.kickRotVis *= Math.exp(-dt * 6);
    if (this.pumpT >= 0) {
      const prevPump = this.pumpT;
      this.pumpT += dt;
      if (prevPump < 0.14 && this.pumpT >= 0.14 && this.def.id === "shotgun") this.sfx("pump");
      if (this.pumpT > 0.5) this.pumpT = -1;
    }
    if (Number.isFinite(pad.aimX) && Number.isFinite(pad.aimY)) {
      this.lookX = pad.aimX;
      this.lookY = pad.aimY;
    }
    if (!this.alive) {
      this.grenadeQueued = false;
      this.vy += GRAVITY * dt;
      this.y += this.vy * dt;
      this.fight.step(dt);
      this.after(dt);
      return;
    }
    if (this.fight.stun > 0) {
      if (this.fight.holding) this.fight.dropHeld();
      pad = {
        ...pad,
        left: false, right: false, jump: false, jumpPressed: false,
        down: false, downPressed: false,
        fire: false, firePressed: false,
        alt: false, altPressed: false,
        slot: -1, cycle: 0,
      };
    }
    if (pad.slot === 100) {
      const exclusive = WEAPONS.findIndex((weapon, index) => index >= FREE_WEAPON_COUNT && !this.banned.has(weapon.id));
      pad.slot = exclusive >= 0 ? exclusive : -1;
    }
    if (this.charge < 0 && pad.slot >= 0 && pad.slot < WEAPONS.length && !this.banned.has(WEAPONS[pad.slot].id)) {
      if (pad.slot !== this.weapon) {
        this.weapon = pad.slot;
        this.flaming = false;
        this.cd = Math.max(this.cd, 0.12);
        this.sfx("switch");
      }
    }
    if (this.charge < 0 && pad.cycle) {
      let next = this.weapon;
      for (let i = 0; i < WEAPONS.length; i++) {
        next = (next + pad.cycle + WEAPONS.length) % WEAPONS.length;
        if (!this.banned.has(WEAPONS[next].id)) break;
      }
      if (next !== this.weapon) {
        this.weapon = next;
        this.cd = Math.max(this.cd, 0.12);
        this.sfx("switch");
      }
    }
    if (this.fight.holding && this.def.kind !== "magnet") this.fight.dropHeld();
    if (this.intro > 0.35) this.move(dt, pad);
    if (this.intro > 0.6) this.weapons(dt, pad);
    else if (this.drone) this.stepDrone(dt, pad);
    this.projectiles(dt);
    this.drill.step(dt);
    this.fight.step(dt);
    this.letters.step(dt);
    this.chunks.update(dt, (slab, x, y, speed) => this.chunkImpact(slab, x, y, speed));
    this.fire.step(dt, this.world, (x, y, kind) => this.puffFire(x, y, kind));
    this.flushBurns(dt);
    this.parts.step(dt, this.world, this.fire);
    this.scorchLetters();
    this.camera(dt);
    this.audio?.loop("jetLoop", this.thrusting);
    this.audio?.loop("flameLoop", this.flaming);
    this.audio?.loop("fireLoop", this.fire.cells.size > 0);
    this.audio?.loop("droneLoop", !!this.drone && this.drone.state === 2);
    this.audio?.loop("drillGrind", this.drill.grinding(), 0.42);
  }

  private move(dt: number, pad: Pad) {
    const dir = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
    const jet = this.jetOn && !this.grounded;
    const max = dir * (jet ? JET_SPEED : RUN) * (this.fight.holding ? MAGNET_SLOW : 1);
    let accel: number;
    if (this.grounded) accel = dir !== 0 && Math.sign(max - this.vx) === Math.sign(max || 1) ? GROUND_ACCEL : GROUND_DECEL;
    else if (jet) accel = dir !== 0 ? JET_ACCEL : JET_DECEL;
    else accel = dir !== 0 ? AIR_ACCEL : AIR_DECEL;
    if (!this.grounded && Math.abs(this.vx) > (jet ? JET_SPEED : RUN) && Math.sign(this.vx) === dir) accel = 200;
    const diff = max - this.vx;
    this.vx += Math.sign(diff) * Math.min(Math.abs(diff), accel * dt);
    const aimDx = pad.aimX - this.x;
    const aimDy = pad.aimY - (this.y - 40);
    if (Math.abs(aimDx) > 2) this.facing = aimDx >= 0 ? 1 : -1;
    this.aim = Math.hypot(aimDx, aimDy) < 24 ? this.aim : Math.atan2(aimDy, aimDx);
    this.buffer = pad.jumpPressed ? JUMP_BUFFER : Math.max(0, this.buffer - dt);
    this.coyote = this.grounded ? COYOTE : Math.max(0, this.coyote - dt);
    if (pad.downPressed && this.grounded && this.y < this.world.H - 1) {
      this.dropT = 0.22;
      this.dropFrom = this.y;
      this.grounded = false;
      this.coyote = 0;
      this.y += 1;
      this.vy = Math.max(this.vy, 60);
    }
    if (this.buffer > 0 && this.coyote > 0) {
      this.buffer = 0;
      this.coyote = 0;
      this.vy = -JUMP_VY;
      this.grounded = false;
      this.jumpHeld = true;
      this.jumps = 1;
      this.sfx("jump");
    } else if (this.buffer > 0 && !this.grounded && this.jumps > 0 && !this.jetOn) {
      this.buffer = 0;
      this.jumps--;
      this.vy = -FLIP_VY;
      this.jumpHeld = true;
      this.flipT = 0;
      this.sfx("flip");
    }
    if (this.jumpHeld && !pad.jump && this.vy < 0 && !this.jetOn) {
      this.vy *= 0.5;
      this.jumpHeld = false;
    }
    if (this.vy >= 0) this.jumpHeld = false;
    this.holdT = pad.jump ? this.holdT + dt : 0;
    if (!this.grounded && !this.jetOn && pad.jump && !pad.down && this.holdT > 0.12 && (this.vy > -150 || this.holdT > 0.45)) {
      this.jetOn = true;
      this.jumps = 0;
      this.jumpHeld = false;
      if (this.packT < 0 || this.stowT >= 0) {
        this.packT = 0;
        this.stowT = -1;
        this.sfx("jetDeploy");
      }
    }
    if (this.packT >= 0) {
      const prev = this.packT;
      this.packT += dt;
      if (prev < JET_IGNITE && this.packT >= JET_IGNITE) this.sfx("jetIgnite");
    }
    this.thrusting = this.jetOn && !this.grounded && pad.jump && !pad.down && this.packT >= JET_IGNITE;
    this.jetK += ((this.thrusting ? 1 : 0) - this.jetK) * Math.min(1, dt * (this.thrusting ? 11 : 7));
    if (this.jetK < 0.01) this.jetK = 0;
    const dropping = pad.down && !this.grounded;
    if (!this.grounded) {
      let g = this.vy < 0 && this.jumpHeld ? GRAVITY * 0.92 : GRAVITY * (this.vy > 0 ? 1.12 : 1);
      this.vy = Math.min(1150, this.vy + g * dt);
      if (this.jetK > 0) {
        this.vy -= JET_THRUST * this.jetK * dt;
        if (this.vy < -JET_MAX_RISE) this.vy += (-JET_MAX_RISE - this.vy) * Math.min(1, dt * 8);
      }
      if (dropping) this.vy = Math.min(1250, this.vy + GRAVITY * 0.6 * dt);
    }
    let nx = Math.max(BODY_HALF, Math.min(this.world.W - BODY_HALF, this.x + this.vx * dt));
    if (nx !== this.x + this.vx * dt) this.vx = 0;
    this.dropT = Math.max(0, this.dropT - dt);
    if (this.grounded) {
      let surface = this.world.findSurface(nx - BODY_HALF, nx + BODY_HALF, this.y - SNAP_UP, this.y + SNAP_DOWN);
      if (Number.isNaN(surface) && this.world.solid(Math.floor(nx / CELL), Math.floor((this.y + 1) / CELL))) {
        surface = this.world.findSurface(nx - BODY_HALF, nx + BODY_HALF, this.y - 56, this.y);
      }
      this.x = nx;
      if (!Number.isNaN(surface)) this.y = surface;
      else {
        this.grounded = false;
        this.vy = 0;
      }
    } else {
      const ny = this.y + this.vy * dt;
      let landed = false;
      if (this.vy >= 0 && !dropping) {
        const from = this.dropT > 0 ? Math.max(this.y, this.dropFrom + 3) : this.y;
        const surface = this.world.findSurface(nx - BODY_HALF, nx + BODY_HALF, from, ny);
        if (!Number.isNaN(surface)) {
          this.x = nx;
          this.y = surface;
          landed = true;
        }
      }
      if (!landed && ny >= this.world.GH * CELL) {
        this.x = nx;
        this.y = this.world.GH * CELL;
        landed = true;
      }
      if (!landed) {
        this.x = nx;
        this.y = ny;
        if (this.y - BODY_H < -400) {
          this.y = -400 + BODY_H;
          this.vy = Math.max(0, this.vy);
        }
      } else {
        const hit = this.vy;
        this.vy = 0;
        this.grounded = true;
        this.jumps = 1;
        this.dropT = 0;
        this.jetOn = false;
        this.flipT = -1;
        this.sfx("land", 1 - Math.min(0.3, hit / 4000));
        this.tumbleV = 0;
      }
    }
    this.airTime = this.grounded ? 0 : this.airTime + dt;
    this.groundT = this.grounded ? this.groundT + dt : 0;
    if (this.packT >= 0 && this.grounded && this.groundT > 0.45 && this.stowT < 0) {
      this.stowT = 0;
      this.sfx("jetStow");
    }
    if (this.stowT >= 0) {
      this.stowT += dt;
      if (this.stowT > 0.22) {
        this.stowT = -1;
        this.packT = -1;
      }
    }
    if (this.flipT >= 0) {
      this.flipT += dt / 0.42;
      if (this.flipT >= 1) this.flipT = -1;
    }
    if (this.grounded) {
      const target = Math.round(this.tumble / (Math.PI * 2)) * Math.PI * 2;
      this.tumble += (target - this.tumble) * Math.min(1, dt * 18);
      if (Math.abs(target - this.tumble) < 0.01) this.tumble = 0;
      if (Math.abs(this.vx) > 40) this.phase += (this.vx * this.facing >= 0 ? 1 : -1) * Math.abs(this.vx) * dt / 46;
    } else {
      this.tumble += this.tumbleV * dt;
      this.tumbleV -= this.tumbleV * 1.2 * dt;
    }
    if (this.grounded && Math.abs(this.vx) > 150 && Math.floor(this.phase * 2) !== Math.floor((this.phase - Math.abs(this.vx) * dt / 46) * 2)) this.sfx("step", 0.9 + Math.random() * 0.2);
  }

  /** Original player.impulse: add velocity, leave the ground on a hard upward hit, tumble past 520. */
  private impulse(ix: number, iy: number) {
    this.vx += ix;
    this.vy += iy;
    if (iy < -120) {
      this.grounded = false;
      this.coyote = 0;
    }
    const mag = Math.hypot(ix, iy);
    if (mag > 520) this.tumbleV += (ix >= 0 ? 1 : -1) * Math.min(22, mag / 60);
  }

  /**
   * Shove this stickman away from a blast. `reach` is the full radius.
   * `lift` is the extra upward share (0.4 for explosions, 0.3 for the meteor).
   * Returns whether the body was inside the radius.
   */
  private selfBlast(x: number, y: number, reach: number, power: number, lift: number) {
    if (!this.alive) return false;
    const py = this.y - BODY_H * 0.45;
    const dx = this.x - x;
    const dy = py - y;
    const dist = Math.hypot(dx, dy) || 1;
    if (dist >= reach) return false;
    const mag = power * Math.pow(1 - dist / reach, 0.75);
    this.impulse((dx / dist) * mag, (dy / dist) * mag - mag * lift);
    return true;
  }

  private beginGrenade() {
    if (this.drone && this.def.kind === "drone") {
      this.dive();
      return;
    }
    this.throwT = 0;
    this.thrown = false;
    this.grenadeCd = GRENADE_CD;
    if (this.posed) this.posed.ready = false;
  }

  private weapons(dt: number, pad: Pad) {
    this.cd -= dt;
    this.grenadeCd -= dt;
    this.droneCd = Math.max(0, this.droneCd - dt);
    const def = this.def;
    if (def.kind === "magnet" || def.kind === "punch") {
      this.fight.use(dt, pad.fire, pad.firePressed, def.kind);
    } else if (def.kind === "drill") {
      if (pad.firePressed && this.drill.ready()) this.fireDrill(def);
    } else if (def.kind === "flame") {
      if (pad.fire) {
        if (!this.flaming) {
          this.flaming = true;
          this.flameBurst = true;
          this.sfx("flameStart");
        }
        this.fireWeapon(def);
      } else if (this.flaming) {
        this.flaming = false;
        this.sfx("flameStop");
      }
    } else if (def.kind === "rail") {
      if (this.charge >= 0) {
        this.charge += dt / 0.38;
        if (this.charge >= 1) {
          this.charge = -1;
          this.rail();
        }
      } else if ((pad.firePressed || (pad.fire && this.cd < -0.12)) && this.cd <= 0) {
        this.charge = 0;
        this.cd = def.cd;
        this.sfx("railCharge");
      }
    } else if (def.auto) {
      if (pad.fire && this.cd <= 0) this.fireWeapon(def);
    } else if ((pad.firePressed || (pad.fire && this.cd < -0.12)) && this.cd <= 0) {
      this.fireWeapon(def);
    }
    const canThrow = this.grenadeCd <= 0 && this.throwT < 0 && this.charge < 0 && !this.fight.grenadeBlocked();
    if (!canThrow) this.grenadeQueued = false;
    else if (pad.altPressed && this.fight.busy()) this.grenadeQueued = true;
    else if (pad.altPressed || (this.grenadeQueued && !this.fight.busy())) {
      this.grenadeQueued = false;
      this.beginGrenade();
    }
    if (this.throwT >= 0) {
      // The rig was posed at the start of this step, before the wind-up advances.
      const shown = this.throwT;
      this.throwT += dt / 0.34;
      if (!this.thrown && this.throwT >= 0.47) this.releaseGrenade(shown);
      if (this.throwT >= 1) this.throwT = -1;
    }
    if (this.drone) this.stepDrone(dt, pad);
  }

  private pose(): Pose {
    if (this.posed?.ready) return this.posed;
    const pose: Pose = {
      x: this.x, y: this.y, facing: this.facing, aim: this.aim, vx: this.vx, vy: this.vy, grounded: this.grounded,
      phase: this.phase, jet: this.jetOn && !this.grounded ? Math.max(0.35, this.jetK) : 0, flip: this.flipT, tumble: this.tumble, skin: this.skin,
      dt: DT, t: this.t, kick: this.recoil, kickRot: this.kickRotVis, who: "local",
      holdDist: this.def.holdDist, holdDrop: this.def.holdDrop, twoHand: this.def.twoHanded, grasp: this.def.grasp,
      throwT: this.throwT,
      cast: this.castT,
      recover: this.recoverT,
      ground: (x, y) => this.world.groundNear(x, y),
    };
    this.posed = pose;
    return pose;
  }

  private aimPose(): Pose {
    const pose = this.pose();
    if (!pose.ready) {
      syncStick(pose, this.held);
      pose.ready = true;
    }
    return pose;
  }

  private fireDrill(def: WeaponDef) {
    this.shots++;
    const pose = this.aimPose();
    const muzzle = muzzleOf(pose, this.held);
    const ang = this.aim;
    this.kick(def, ang);
    this.sfx("drillShot");
    this.drill.launch(muzzle.x, muzzle.y, ang, this.vx, this.vy, false, this.lookX, this.lookY);
    this.onShot?.("drill", { x: muzzle.x, y: muzzle.y, a: ang, vx: this.vx, vy: this.vy });
  }

  /**
   * The rocket's fireball, lights, chunks and ground fire, at 1.3×.
   * Screen flash is 0.9. The white core is about three frames. No damage.
   */
  private drillSpectacle(x: number, y: number) {
    const radius = ROCKET_R * 1.3;
    const power = 1.3;
    const world = this.world;
    const blastRim = world.rim(3);
    this.heatRim(blastRim, 2.8, 0.92);
    this.seedRims(x, y, radius, Math.round(14 * power));
    this.fire.ignite(world, x, y - 4);
    this.flashes.push({ x, y, r: (radius / CELL) * 1.05, life: 0.05, max: 0.05 });
    this.flashScreen(0.9, "255,246,222");
    this.rings.push({ x, y, r0: radius * 0.18, r1: radius * 1.15, life: 0.16, max: 0.16, thick: 1.5, color: WHITE });
    this.rings.push({ x, y, r0: radius * 0.32, r1: radius * 1.85, life: 0.26, max: 0.26, thick: 2.1, color: YELLOW });
    this.rings.push({ x, y, r0: radius * 0.46, r1: radius * 2.7, life: 0.36, max: 0.36, thick: 2.6, color: ORANGE });
    this.lights.push({ x, y, r: radius * 3.4, k: 0.6 * power, life: 0.7, max: 0.7 });
    this.lights.push({ x, y, r: radius * 1.6, k: 0.78, life: 0.08, max: 0.08 });
    const cells = radius / CELL;
    const fireball = (px: number, py: number, vx: number, vy: number, life: number, sizeCells: number, puff: 0 | 1 | 2 = 0) => {
      this.parts.spawn({
        x: px, y: py, vx, vy, life, max: life,
        size: sizeCells * CELL,
        color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff,
      });
    };
    for (let n = 0; n < Math.round(5 * power); n++) {
      fireball(x + (Math.random() - 0.5) * 10, y + (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 80, -20 - Math.random() * 60, 0.18 + Math.random() * 0.12, cells * (0.4 + Math.random() * 0.15));
    }
    for (let n = 0; n < Math.round(28 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.55;
      const speed = (80 + Math.random() * 340) * power;
      fireball(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * speed, Math.sin(ang) * speed * 0.8 - 80,
        0.55 + Math.random() * 0.6, cells * (0.22 + Math.random() * 0.24),
        Math.random() < 0.45 ? 2 : 0,
      );
    }
    for (let n = 0; n < Math.round(5 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.1 + Math.random() * 0.4);
      this.parts.smoke(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * 60, Math.sin(ang) * 40 - 60,
        cells * (0.15 + Math.random() * 0.11) * CELL, 0.9 + Math.random() * 0.6, "#221d26",
      );
    }
    for (let n = 0; n < Math.round(5 * power); n++) {
      fireball(x + (Math.random() - 0.5) * radius * 0.5, y, (Math.random() - 0.5) * 50, -200 - Math.random() * 160, 0.8 + Math.random() * 0.5, cells * (0.24 + Math.random() * 0.12), Math.random() < 0.6 ? 2 : 0);
    }
    for (let n = 0; n < Math.round(80 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = (320 + Math.random() * 880) * power;
      const life = 0.18 + Math.random() * 0.52;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 140,
        life, max: life, size: 1.2,
        color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
    for (let n = 0; n < Math.round(40 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 300;
      const life = 1.2 + Math.random() * 2;
      this.parts.spawn({
        x: x + Math.cos(ang) * radius * 0.4, y: y + Math.sin(ang) * radius * 0.4,
        vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 150,
        life, max: life, size: 1,
        color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
      });
    }
    const chunks = Math.round((9 + Math.random() * 5) * power);
    for (let n = 0; n < chunks; n++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.7;
      const speed = (380 + Math.random() * 440) * power;
      const life = 0.7 + Math.random() * 0.8;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed,
        life, max: life, size: 3.2 * power,
        color: "#ffe26b", grav: GRAVITY * 0.8, rubble: false, glow: true, kind: "chunk",
      });
    }
    for (let n = 0; n < Math.round(10 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.1 + Math.random() * 0.6);
      const life = 1.8 + Math.random() * 1.2;
      this.parts.smoke(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * (30 + Math.random() * 170), Math.sin(ang) * (20 + Math.random() * 100) - 70,
        cells * (0.26 + Math.random() * 0.18) * CELL, life,
      );
    }
    for (let n = 0; n < Math.round(3 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.4 + Math.random() * 0.7);
      this.pops.push({
        x: x + Math.cos(ang) * dist,
        y: y + Math.sin(ang) * dist,
        t: 0.07 + Math.random() * 0.27,
        s: (0.45 + Math.random() * 0.25) * power,
      });
    }
  }

  private drillPlayers(x: number, y: number, radius: number, dmg: number) {
    if (!this.online) return;
    for (const remote of this.remotes) {
      if (!remote.alive) continue;
      const rd = Math.hypot(remote.x - x, remote.y - 28 - y);
      if (rd < radius) this.onShot?.("hitp", { to: remote.id, dmg: dmg * (1 - rd / radius), w: "drill" });
    }
  }

  private fireWeapon(def: WeaponDef) {
    if (def.kind === "tesla" && this.coils.length) return;
    this.cd = def.cd;
    // The flamethrower only counts the opening burst. Everything else counts here.
    if (def.kind !== "flame") this.shots++;
    const pose = this.aimPose();
    const muzzle = muzzleOf(pose, this.held);
    const ang = this.aim;
    if (def.kind === "bullet") {
      this.sfx(def.id === "shotgun" ? "shotgun" : def.id === "smg" ? "smg" : "pistol");
      const aimRange = Math.max(50, Math.hypot(this.aimX() - muzzle.x, this.aimY() - muzzle.y));
      const assist = def.id === "shotgun" && !this.online
        ? this.fight.aimPoint(muzzle.x, muzzle.y, ang, 0.2, Math.min(240, aimRange * 1.25 + 24))
        : null;
      const volley = def.id === "shotgun" ? ++this.shotVolley : 0;
      for (let i = 0; i < def.pellets; i++) {
        const multi = def.pellets > 1;
        const guided = assist !== null && i === 0;
        const spread = guided ? 0 : (Math.random() - 0.5) * def.spread * 2;
        const speed = def.speed * (guided ? 1 : multi ? 0.82 + Math.random() * 0.26 : 1);
        const a = guided && assist ? Math.atan2(assist.y - muzzle.y, assist.x - muzzle.x) : ang + spread;
        const range = guided && assist
          ? assist.dist + 28
          : aimRange * (multi ? 0.86 + Math.random() * 0.26 : 1);
        this.bullets.push({
          x: muzzle.x, y: muzzle.y, vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
          life: 1.2, pierce: def.pierce, dmg: def.dmg, dist: 0, range, weapon: def.id, heavy: def.pellets > 1, owner: this.id, ghost: false,
          guide: guided, volley: volley || undefined, stand: assist ? assist.dist : undefined,
        });
      }
      if (def.id === "shotgun") {
        this.pumpT = 0;
        for (let n = 0; n < 6; n++) {
          const a = ang + Math.PI + (Math.random() - 0.5);
          const s = 60 + Math.random() * 260;
          this.parts.smoke(muzzle.x, muzzle.y, Math.cos(a) * s, Math.sin(a) * s, (2 + Math.random() * 1.6) * CELL, 0.6 + Math.random() * 0.7);
        }
      }
      this.kick(def, ang);
      this.onShot?.("shot", { w: def.id, x: muzzle.x, y: muzzle.y });
    } else if (def.kind === "rocket") {
      this.sfx("rocket");
      const range = Math.max(50, Math.hypot(this.aimX() - muzzle.x, this.aimY() - muzzle.y));
      this.rockets.push({ x: muzzle.x, y: muzzle.y, a: ang, speed: def.speed, t: 0, range, dist: 0, weapon: "rocket", ghost: false, wobble: Math.random() * 6 });
      for (let n = 0; n < 10; n++) {
        const a = ang + Math.PI + (Math.random() - 0.5);
        const s = 60 + Math.random() * 260;
        this.parts.smoke(muzzle.x, muzzle.y, Math.cos(a) * s, Math.sin(a) * s, (2 + Math.random() * 1.6) * CELL, 0.6 + Math.random() * 0.7);
      }
      for (let n = 0; n < 5; n++) {
        const a = ang + Math.PI + (Math.random() - 0.5) * 0.6;
        const s = 100 + Math.random() * 160;
        this.parts.flamePuff(muzzle.x, muzzle.y, Math.cos(a) * s, Math.sin(a) * s, (1.5 + Math.random() * 1.1) * CELL, 0.1 + Math.random() * 0.1);
      }
      this.kick(def, ang);
      this.onShot?.("rkt", { x: muzzle.x, y: muzzle.y, a: ang, r: range });
    } else if (def.kind === "mirv") {
      this.sfx("tooMuch");
      const mvx = Math.cos(ang) * def.speed + this.vx * 0.3;
      const mvy = Math.sin(ang) * def.speed + this.vy * 0.3;
      this.mirvs.push({ x: muzzle.x, y: muzzle.y, vx: mvx, vy: mvy, a: ang, state: 0, t: 0, ct: 0, bounces: 0, ghost: false });
      for (let n = 0; n < 16; n++) {
        const a = ang + Math.PI + (Math.random() - 0.5) * 1.2;
        const s = 80 + Math.random() * 340;
        this.parts.smoke(muzzle.x, muzzle.y, Math.cos(a) * s, Math.sin(a) * s, (2.4 + Math.random() * 1.6) * CELL, 0.7 + Math.random() * 0.7, "#5b5566");
      }
      for (let n = 0; n < 8; n++) {
        const a = ang + Math.PI + (Math.random() - 0.5) * 0.7;
        const s = 120 + Math.random() * 200;
        const life = 0.12 + Math.random() * 0.12;
        this.parts.spawn({
          x: muzzle.x, y: muzzle.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
          life, max: life, size: (2 + Math.random() * 1.2) * CELL,
          color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball",
        });
      }
      for (let n = 0; n < 12; n++) {
        const a = ang + (Math.random() - 0.5);
        const s = 80 + Math.random() * 180;
        this.parts.smoke(muzzle.x, muzzle.y, Math.cos(a) * s, Math.sin(a) * s, (2 + Math.random() * 1.4) * CELL, 0.6 + Math.random() * 0.6, "#5b5566");
      }
      this.kick(def, ang);
      this.onShot?.("mirv", { x: muzzle.x, y: muzzle.y, vx: mvx, vy: mvy, a: ang });
    } else if (def.kind === "flame") {
      const burst = this.flameBurst;
      this.flameBurst = false;
      if (burst) this.shots++;
      const aimDx = this.aimX() - muzzle.x;
      const aimDy = this.aimY() - muzzle.y;
      const flameAng = Math.hypot(aimDx, aimDy) < 24 ? this.aim : Math.atan2(aimDy, aimDx);
      const count = burst ? 14 : def.pellets;
      this.parts.flame(muzzle.x, muzzle.y, flameAng, count, def.speed, burst, this.vx, this.vy);
      this.lights.push({
        x: muzzle.x + Math.cos(flameAng) * 30,
        y: muzzle.y + Math.sin(flameAng) * 30,
        r: burst ? 150 : 90,
        k: burst ? 0.9 : 0.5,
        life: 0.05,
        max: 0.05,
        color: ORANGE,
      });
      // Visual kick only. kickRot would twist the barrel down into the bar the player stands on.
      this.recoil = Math.max(this.recoil, def.kick + Math.random() * 0.6);
      this.trauma = Math.min(1, this.trauma + (burst ? 0.08 : def.shake));
    } else if (def.kind === "tesla") {
      this.sfx("teslaThrow");
      const tx = Math.max(12, Math.min(this.world.W - 12, this.aimX()));
      const ty = Math.max(12, Math.min(this.world.H - 12, this.aimY()));
      const dx = tx - muzzle.x;
      const dy = ty - muzzle.y;
      const dist = Math.hypot(dx, dy);
      const flight = Math.max(0.1, Math.min(0.28, dist / Math.max(80, def.speed)));
      const vx = dx / flight;
      const vy = (dy - 0.5 * GRAVITY * flight * flight) / flight;
      this.coils.push(launchCoil(muzzle.x, muzzle.y, vx, vy, tx, ty, flight));
      throwCrackle(this.ordnanceHost(), this.bolts, muzzle.x, muzzle.y, tx, ty);
      this.kick(def, ang);
    } else if (def.kind === "meteor") {
      const tx = Math.max(0, Math.min(this.world.W, this.aimX()));
      const ty = Math.max(-200, Math.min(this.world.H, this.aimY()));
      const fromRight = this.x <= this.camX + this.viewW * 0.5;
      // The ember leaves the gem at the top of the swing, not from where the staff rested.
      const raised = castPose(ang, this.facing || 1, 0.05).aim;
      const reach = 50;
      const pivotX = muzzle.x - Math.cos(ang) * reach;
      const pivotY = muzzle.y - Math.sin(ang) * reach;
      const ox = pivotX + Math.cos(raised) * reach;
      const oy = pivotY + Math.sin(raised) * reach;
      // Effects budget: at most METEOR_MAX live at once. Spent ones (crater smoke, fading rock) go first.
      while (this.meteors.length >= METEOR_MAX) {
        const spent = this.meteors.findIndex((m) => m.phase === "gone" || m.phase === "fade" || m.phase === "roll");
        this.meteors.splice(spent >= 0 ? spent : 0, 1);
      }
      this.meteors.push(markMeteor(ox, oy, tx, ty, fromRight, this.camY, this.viewW));
      this.kick(def, ang);
      this.castT = 0;
      this.gemFlash = 1;
      this.sfx("meteorCast");
    } else if (def.kind === "drone") {
      if (!this.drone && this.droneCd <= 0) {
        const pack = packPoint(pose);
        this.drone = {
          x: pack.x, y: pack.y - 6,
          vx: -this.facing * 60 + this.vx * 0.5, vy: -520,
          a: this.facing > 0 ? 0.3 : Math.PI - 0.3,
          battery: 12, state: 1, rotor: 0, gun: 0, firing: false, cd: 0.07, beep: 0.4, t: 0, face: this.facing || 1,
          tilt: 0, tiltV: 0, age: 0,
          gunA: this.facing > 0 ? 0.3 : Math.PI - 0.3, barrel: 0, spin: 0, kick: 0,
        };
        this.sfx("droneBeep");
        for (let n = 0; n < 9; n++) {
          this.parts.smoke(this.drone.x, this.drone.y, (Math.random() - 0.5) * 140, 30 + Math.random() * 80, (1.2 + Math.random()) * CELL, 0.3 + Math.random() * 0.3);
        }
      }
    }
  }

  private hitPlayer(x: number, y: number, dmg: number, weapon: string): boolean {
    if (!this.online) return false;
    for (const remote of this.remotes) {
      if (!remote.alive) continue;
      if (Math.hypot(x - remote.x, y - (remote.y - 28)) < 18) {
        this.onShot?.("hitp", { to: remote.id, dmg, w: weapon });
        return true;
      }
    }
    return false;
  }

  private hitSound(x: number, y: number, heavy: boolean) {
    const world = this.world;
    const owner = world.owner[Math.floor(y / CELL) * world.GW + Math.floor(x / CELL)] ?? -1;
    const block = owner >= 0 ? world.blocks[owner] : undefined;
    if (block?.kind === "image") return "hitImage";
    if (block?.kind === "box" || heavy) return "hitBlock";
    return "hitPaper";
  }

  /** Replay a terrain batch with the log detached, so it is not recorded again. */
  applyOps(bytes: Uint8Array) {
    this.commitText();
    const saved = this.world.log;
    this.world.log = null;
    let ctx = { kind: 0, a: 0, b: 0, c: 0, d: 0 };
    try {
      readOps(bytes, {
        clear: (cell) => {
          this.world.applyClear(cell, false);
          this.fire.cells.delete(cell);
          if (this.world.carved.length < 60000) this.world.carved.push(cell);
        },
        debris: (cell, color) => this.world.bakeIndex(cell, color, false),
        ignite: (cell) => this.fire.show(cell),
        letter: (index, state) => {
          if (!state) this.letters.detach(index, 0, 0, -1, true, null);
          else this.letters.detach(index, state.vx, state.vy, state.burn, true, state);
        },
        block: (index, vx, vy, av) => {
          const out = this.chunks.detach(index, vx, vy, av, true);
          for (let i = 0; i < out.crumbs.length && i < 900; i += 3) {
            this.parts.chip(out.crumbs[i], out.crumbs[i + 1], vx * 0.4, vy * 0.4 - 40, out.crumbs[i + 2]);
          }
        },
        ctx: (kind, a, b, c, d) => {
          this.world.beginCarve();
          ctx = { kind, a, b, c, d };
        },
        heat: (rim, _a, _b, flag) => {
          if (ctx.kind === 1) this.world.scorch(ctx.a, ctx.b, ctx.c * 0.92, ctx.c * 1.7, 1);
          if (flag) {
            const edge = this.world.rim(rim);
            this.world.char(edge.cells, edge.ring);
          }
        },
      });
    } finally {
      this.world.log = saved;
    }
  }

  applyOpsB64(payload: string) {
    if (!payload) return;
    this.applyOps(b64ToBytes(payload));
  }

  /**
   * The first readback commits fillText. clearRect issued before that
   * readback is dropped, so a late joiner would keep different letters.
   */
  private textCommitted = false;
  private commitText() {
    if (this.textCommitted) return;
    this.textCommitted = true;
    this.world.tx.getImageData(0, 0, this.world.text.width, this.world.text.height);
  }

  private aimX() {
    return this.lookX;
  }
  private aimY() {
    return this.lookY;
  }

  private kick(def: WeaponDef, ang: number) {
    const ground = this.grounded ? 0.45 : 1;
    this.vx -= Math.cos(ang) * def.push * ground;
    if (!this.grounded) this.vy -= Math.sin(ang) * def.push * 0.35;
    this.trauma = Math.min(1, this.trauma + def.shake);
    this.shake = def.flash;
    this.recoil = def.kick;
    this.kickRotVis = def.kickRot;
    if (this.posed) this.posed.ready = false;
  }

  /** Wind up, then let go from the hand on a ballistic arc toward the cursor. */
  private releaseGrenade(poseT = this.throwT) {
    this.thrown = true;
    const saved = this.throwT;
    this.throwT = poseT;
    if (this.posed) this.posed.ready = false;
    const pose = this.aimPose();
    this.throwT = saved;
    const hand = handOf(pose, this.held, this.def.twoHanded);
    const dx = this.aimX() - hand.x;
    const dy = this.aimY() - hand.y;
    const z = Math.max(0.32, Math.min(0.85, Math.hypot(dx, dy) / 620));
    const nade: Nade = {
      x: hand.x, y: hand.y,
      vx: dx / z + this.vx * 0.25,
      vy: dy / z - 950 * z,
      a: 0, av: this.facing * 14, fuse: 1.55, grounded: false, weapon: "grenade", ghost: false,
    };
    this.nades.push(nade);
    this.sfx("click");
    this.onShot?.("gren", { x: nade.x, y: nade.y, vx: nade.vx, vy: nade.vy, av: nade.av });
  }

  private dive() {
    if (!this.drone || this.drone.state !== 2) return;
    this.drone.state = 4;
    this.drone.t = 0;
    this.drone.firing = false;
    this.sfx("droneDive");
  }

  private flashScreen(amount: number, color: string) {
    if (amount >= this.screenFlash) this.screenTint = color;
    this.screenFlash = Math.min(1, Math.max(this.screenFlash, amount));
  }

  private rail() {
    this.shots++;
    const pose = this.pose();
    const muzzle = muzzleOf(pose, null);
    const ang = this.aim;
    const world = this.world;
    const dirX = Math.cos(ang);
    const dirY = Math.sin(ang);
    let length = 4000;
    if (dirX > 0) length = Math.min(length, (world.W - muzzle.x) / dirX);
    if (dirX < 0) length = Math.min(length, -muzzle.x / dirX);
    if (dirY > 0) length = Math.min(length, (world.H - muzzle.y) / dirY);
    if (dirY < 0) length = Math.min(length, (-200 - muzzle.y) / dirY);
    length = Math.max(0, length);
    const x1 = muzzle.x + dirX * length;
    const y1 = muzzle.y + dirY * length;
    this.beams.push({ x0: muzzle.x, y0: muzzle.y, x1, y1, life: 0.42, max: 0.42 });
    const sparks = Math.min(220, Math.floor(length / 7));
    for (let i = 0; i < sparks; i++) {
      const along = Math.random() * length;
      const side = (Math.random() < 0.5 ? -1 : 1) * (Math.PI / 2) + (Math.random() - 0.5);
      const kick = 60 + Math.random() * 260;
      this.parts.spawn({
        x: muzzle.x + dirX * along, y: muzzle.y + dirY * along,
        vx: Math.cos(Math.atan2(dirY, dirX) + side) * kick + dirX * 80,
        vy: Math.sin(Math.atan2(dirY, dirX) + side) * kick + dirY * 80,
        life: 0.15 + Math.random() * 0.4, max: 0.55, size: 1.2,
        color: "#7ff3ff", grav: 0, rubble: false, glow: true, kind: "ion",
      });
    }
    for (let i = 0; i < Math.min(60, sparks / 3); i++) {
      const along = Math.random() * length;
      this.parts.smoke(
        muzzle.x + dirX * along, muzzle.y + dirY * along,
        (Math.random() - 0.5) * 20, -6 - Math.random() * 18,
        (1.6 + Math.random() * 1.4) * CELL, 0.8 + Math.random() * 0.8, "#4b6e86",
      );
    }
    this.flashScreen(0.9, "226,252,255");
    const owners = new Set<number>();
    world.beginCarve();
    world.log?.ctx(4, dirX, dirY);
    let chips = 0;
    for (let dist = 0; dist < length; dist += 3) {
      world.carve(muzzle.x + dirX * dist, muzzle.y + dirY * dist, 7, 0.05, (cx, cy, color, mat, owner) => {
        if (owner >= 0) owners.add(owner);
        if (chips >= 1100 || Math.random() >= (mat === BACKGROUND ? 0.1 : 0.3)) return;
        chips++;
        const side = Math.random() < 0.5 ? -1 : 1;
        const kick = 60 + Math.random() * 320;
        this.parts.chip(
          (cx + 0.5) * CELL,
          (cy + 0.5) * CELL,
          -dirY * side * kick + dirX * (50 + Math.random() * 200),
          dirX * side * kick - (40 + Math.random() * 120),
          color,
          2.4,
        );
      });
    }
    // Letters along the beam fly nearest first, up to one shot's share of the page's text.
    const hits: number[] = [];
    this.letters.query(Math.min(muzzle.x, x1) - 10, Math.min(muzzle.y, y1) - 10, Math.max(muzzle.x, x1) + 10, Math.max(muzzle.y, y1) + 10, (i) => {
      const box = this.letters.hitBox(i);
      if (segBox(muzzle.x, muzzle.y, x1, y1, box[0] - 6, box[1] - 6, box[2] + 6, box[3] + 6) >= 0) hits.push(i);
    });
    const along = (i: number) => { const b = this.letters.hitBox(i); return ((b[0] + b[2]) / 2 - muzzle.x) * dirX + ((b[1] + b[3]) / 2 - muzzle.y) * dirY; };
    if (hits.length > 0) {
      const cap = Math.max(12, Math.ceil(RAIL_LETTER_SHARE * Math.min(0.6 * Math.max(1, this.letters.x.length), 2400)));
      if (hits.length > cap) hits.sort((a, b) => along(a) - along(b)).length = cap;
    }
    for (const i of hits) {
      const side = Math.random() < 0.5 ? -1 : 1;
      const kick = 250 + Math.random() * 350;
      this.fling(
        i,
        dirX * (200 + Math.random() * 500) - dirY * side * kick,
        dirY * (200 + Math.random() * 500) + dirX * side * kick - 150,
        (Math.random() - 0.5) * 60,
        Math.random() < 0.55 ? 0 : -1,
      );
    }
    for (const body of this.letters.bodies) {
      if (segBox(muzzle.x, muzzle.y, x1, y1, body.x - body.hw - 4, body.y - body.hh - 4, body.x + body.hw + 4, body.y + body.hh + 4) < 0) continue;
      body.vx += dirX * 600;
      body.vy += dirY * 600 - 200;
      body.av += (Math.random() - 0.5) * 60;
      if (body.burn < 0) body.burn = 0;
    }
    for (const slab of this.chunks.list.slice()) {
      if (slabAlong(slab, muzzle.x, muzzle.y, x1, y1) >= 0) this.chunks.shatter(slab, slab.x, slab.y, 360);
    }
    const rim = world.rim(3);
    world.char(rim.cells, rim.ring);
    this.heatRim(rim, 3.6, 1.05);
    world.log?.heat(3, 3.6, 1.05, true);
    // A piercing line crosses every block on the page. Blocks along it still crack, but
    // only as many break loose as one shot's share allows, nearest first, so a beam
    // through a page-wide container can't carry the whole page with it.
    const budget = RAIL_BREAK_SHARE * Math.min(0.3 * world.totalCells, 120000);
    const rc0 = world.removedCells;
    for (const owner of owners) {
      const block = world.blocks[owner];
      if (!block?.alive) continue;
      let dmg = WEAPONS[4].dmg;
      const shed = block.lost >= block.cells * 0.38;
      if (block.canDetach && (shed || block.damage + dmg >= block.hp) && world.removedCells - rc0 + world.subtreeCells(owner) > budget) {
        if (shed) continue;
        dmg = Math.max(0, block.hp - block.damage - 1);
        if (dmg <= 0) continue;
      }
      world.damageBlock(owner, dmg, dirX, dirY);
    }
    this.fight.clipSeg(muzzle.x, muzzle.y, x1, y1, RAIL_DMG, dirX, dirY);
    this.sfx("rail");
    this.kick(WEAPONS[4], ang);
    this.onShot?.("rail", { x0: muzzle.x, y0: muzzle.y, x1, y1 });
  }

  /** Slew the slung gatling toward the crosshair. The body keeps its own tilt. */
  private aimDrone(d: Drone, pad: Pad, dt: number) {
    const bob = d.state === 2 ? Math.sin(d.age * 4.3) * 1.3 : 0;
    const my = d.y + bob + 12;
    let delta = Math.atan2(pad.aimY - my, pad.aimX - d.x) - d.gunA;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    d.gunA += delta * Math.min(1, dt * 20);
    const face = Math.cos(d.gunA) >= 0 ? 1 : -1;
    if (face !== d.face) {
      d.face = face;
      d.tiltV += face * 3;
    }
    d.spin = pad.fire ? Math.min(1, d.spin + dt / 0.35) : Math.max(0, d.spin - dt / 0.45);
    d.barrel += dt * 34 * d.spin;
    d.kick *= Math.exp(-dt * 22);
  }

  private stepDrone(dt: number, pad: Pad) {
    const d = this.drone;
    if (!d) return;
    d.t += dt;
    d.age += dt;
    d.rotor += dt * 26;
    const lean = Math.max(-1, Math.min(1, d.vx / 460)) * 0.3 + Math.max(-1, Math.min(1, d.vy / 460)) * 0.05 * d.face;
    d.tiltV += ((lean - d.tilt) * 150 - d.tiltV * 14) * dt;
    d.tilt += d.tiltV * dt;
    this.aimDrone(d, pad, dt);
    if (d.state === 1) {
      d.vx -= d.vx * Math.min(1, dt * 4);
      d.vy -= d.vy * Math.min(1, dt * 5.5);
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.t >= 0.5) {
        d.state = 2;
        d.t = 0;
      }
      return;
    }
    if (d.state === 2) {
      let ix = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
      let iy = (pad.down ? 1 : 0) - (pad.jump ? 1 : 0);
      const mag = Math.hypot(ix, iy);
      if (mag > 1) {
        ix /= mag;
        iy /= mag;
      }
      d.vx += ix * 2600 * dt;
      d.vy += iy * 2600 * dt;
      const drag = mag > 0 ? 3 * 0.35 : 3;
      d.vx -= d.vx * Math.min(1, drag * dt);
      d.vy -= d.vy * Math.min(1, drag * dt);
      const sp = Math.hypot(d.vx, d.vy);
      if (sp > 460) {
        d.vx = (d.vx / sp) * 460;
        d.vy = (d.vy / sp) * 460;
      }
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.battery -= dt * (1 + (d.firing ? 0.4 : 0));
      const prevGun = d.gun;
      if (pad.fire) d.gun = Math.min(1, d.gun + dt / 0.28);
      else d.gun = Math.max(0, d.gun - dt / 0.7);
      if (pad.fire && prevGun === 0) this.sfx("gatlingSpin");
      const spinning = pad.fire && d.gun >= 1;
      if (!pad.fire && d.firing) this.sfx("gatlingStop");
      d.firing = spinning;
      d.cd -= dt;
      if (spinning && d.cd <= 0) {
        d.cd = 0.028;
        const a = Math.atan2(pad.aimY - d.y, pad.aimX - d.x) + (Math.random() - 0.5) * 0.05;
        this.bullets.push({
          x: d.x + Math.cos(a) * 16, y: d.y + Math.sin(a) * 16, vx: Math.cos(a) * 2915, vy: Math.sin(a) * 2915, life: 0.6, pierce: 5, dmg: 16,
          dist: 0, range: 520, weapon: "drone", heavy: false, owner: this.id, ghost: false,
        });
        this.sfx("gatling");
      }
      if (d.battery < 3) {
        d.beep -= dt;
        if (d.beep <= 0) {
          d.beep = 0.35;
          this.sfx("droneLowBat");
        }
      }
      if (d.battery <= 0) {
        this.explode(d.x, d.y, 26, 0.9, "drone");
        this.drone = null;
        this.droneCd = 5;
        this.sfx("droneDie");
      }
      return;
    }
    const dx = pad.aimX - d.x;
    const dy = pad.aimY - d.y;
    const dist = Math.hypot(dx, dy) || 1;
    const speed = Math.min(980, 260 + d.t * 2400);
    d.vx += (dx / dist * speed - d.vx) * Math.min(1, dt * 10);
    d.vy += (dy / dist * speed - d.vy) * Math.min(1, dt * 10);
    const diveX = d.x;
    const diveY = d.y;
    d.x += d.vx * dt;
    d.y += d.vy * dt;
    const hit = this.fight.intercept(diveX, diveY, d.x, d.y);
    const struck = hit.t >= 0;
    if (struck || dist < 14 || d.t > 1.2 || d.y > this.world.H) {
      const ex = struck ? diveX + (d.x - diveX) * hit.t : d.x;
      const ey = struck ? diveY + (d.y - diveY) * hit.t : d.y;
      this.explode(ex, ey, 72, 1.5, "drone", struck ? hit.index : -1);
      this.drone = null;
      this.droneCd = 5;
      this.sfx("droneDie");
    }
  }

  private projectiles(dt: number) {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.life -= dt;
      if (b.life <= 0) {
        this.bullets.splice(i, 1);
        continue;
      }
      const speed = Math.hypot(b.vx, b.vy) || 1;
      const dirX = b.vx / speed;
      const dirY = b.vy / speed;
      const travel = Math.min(speed * dt, b.range);
      const x0 = b.x;
      const y0 = b.y;
      let x1 = b.x + dirX * travel;
      let y1 = b.y + dirY * travel;
      const atEnd = travel >= b.range - 0.001;
      if (!b.ghost) {
        const hits: { t: number; kind: number; index: number }[] = [];
        this.letters.query(Math.min(x0, x1), Math.min(y0, y1), Math.max(x0, x1), Math.max(y0, y1), (n) => {
          const box = this.letters.hitBox(n);
          const t = segBox(x0, y0, x1, y1, box[0], box[1], box[2], box[3]);
          if (t >= 0) hits.push({ t, kind: 0, index: n });
        });
        this.letters.bodies.forEach((body, index) => {
          const t = segBox(x0, y0, x1, y1, body.x - body.hw - 1.5, body.y - body.hh - 1.5, body.x + body.hw + 1.5, body.y + body.hh + 1.5);
          if (t >= 0) hits.push({ t, kind: 1, index });
        });
        this.chunks.list.forEach((slab, index) => {
          const t = slabAlong(slab, x0, y0, x1, y1);
          if (t >= 0) hits.push({ t, kind: 2, index });
        });
        hits.sort((a, c) => a.t - c.t);
        const touchT = this.fight.touch(x0, y0, x1, y1);
        for (const hit of hits) {
          if (touchT >= 0 && hit.t >= touchT) break;
          const hx = x0 + (x1 - x0) * hit.t;
          const hy = y0 + (y1 - y0) * hit.t;
          if (hit.kind === 0) {
            this.fling(hit.index, dirX * (230 + Math.random() * 200) + (Math.random() - 0.5) * 80, dirY * (150 + Math.random() * 200) - (90 + Math.random() * 110), (Math.random() - 0.5) * 44, -1);
            b.pierce--;
          } else if (hit.kind === 1) {
            const body = this.letters.bodies[hit.index];
            if (body) {
              body.vx += dirX * 320;
              body.vy += dirY * 320 - 160;
              body.av += (Math.random() - 0.5) * 40;
            }
            b.pierce--;
          } else {
            const slab = this.chunks.list[hit.index];
            if (slab) {
              const scale = Math.min(1, 4000 / Math.max(1, slab.m));
              slab.vx += dirX * (b.heavy ? 90 : 60) * scale;
              slab.vy += dirY * 60 * scale - 30;
              slab.av += ((hx - slab.x) * dirY - (hy - slab.y) * dirX) * 0.004;
              slab.hitFlash = 0.05;
              slab.impacts++;
              this.sfx("hitChunk");
              if (slab.impacts > (slab.fragment ? 3 : 8)) this.chunks.shatter(slab, hx, hy, 200);
            }
            b.pierce = -1;
          }
          if (b.pierce < 0) {
            x1 = hx;
            y1 = hy;
            b.life = 0;
            break;
          }
        }
        let shotDmg = minionShotDmg(b.weapon);
        let budget: { token: number; limit: number } | undefined;
        if (b.weapon === "shotgun" && touchT >= 0) {
          const traveled = b.dist + Math.hypot(x1 - x0, y1 - y0) * Math.max(0, touchT);
          if (!b.guide) shotDmg = Math.max(1, Math.round(shotDmg * Math.max(0.15, 1 - traveled / 115)));
          if (b.volley && b.stand !== undefined && b.stand >= 60) budget = { token: b.volley, limit: 30 };
        }
        const minion = b.life > 0 && touchT >= 0
          ? this.fight.clipSeg(x0, y0, x1, y1, shotDmg, dirX, dirY, budget)
          : -1;
        if (minion >= 0) {
          x1 = x0 + (x1 - x0) * minion;
          y1 = y0 + (y1 - y0) * minion;
          b.life = 0;
        }
        if (this.hitPlayer(x1, y1, PLAYER_BULLET_DMG[b.weapon] ?? b.dmg, b.weapon)) b.life = 0;
      }
      const flown = Math.hypot(x1 - x0, y1 - y0);
      b.dist += flown;
      b.range -= flown;
      b.x = x1;
      b.y = y1;
      if (b.life > 0 && atEnd) {
        b.life = 0;
        const mat = this.world.matAt(b.x, b.y);
        if (mat >= SOLID) this.bulletImpact(b, dirX, dirY);
        else if (mat === BACKGROUND) this.paperHole(b.x, b.y, dirX, dirY, b.heavy ? 2.2 : b.weapon === "drone" ? 3.6 : 2.8);
      }
      if (b.life <= 0 || b.x < -20 || b.x > this.world.W + 20 || b.y < -400 || b.y > this.world.H + 20) this.bullets.splice(i, 1);
    }
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      r.t += dt;
      r.speed = Math.min(1250, r.speed + 1900 * dt);
      const wobble = Math.sin(r.t * 38) * 0.012;
      const dirX = Math.cos(r.a + wobble);
      const dirY = Math.sin(r.a + wobble);
      const step = Math.min(r.speed * dt, r.range);
      const x1 = r.x + dirX * step;
      const y1 = r.y + dirY * step;
      const tailX = r.x - dirX * 8;
      const tailY = r.y - dirY * 8;
      for (let n = 0; n < 2; n++) {
        this.parts.smoke(
          tailX + (Math.random() - 0.5) * 4, tailY + (Math.random() - 0.5) * 4,
          -dirX * (20 + Math.random() * 60) + (Math.random() - 0.5) * 30,
          -dirY * (20 + Math.random() * 60) + (Math.random() - 0.5) * 30,
          (1.5 + Math.random() * 1.3) * CELL, 0.7 + Math.random() * 0.7,
        );
      }
      const nozzle = 1.3 + Math.random() * 0.9;
      this.parts.spawn({
        x: tailX, y: tailY, vx: -dirX * 120, vy: -dirY * 120,
        life: 0.07, max: 0.07, size: nozzle * CELL,
        color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball",
      });
      if (Math.random() < 0.3) {
        this.parts.spawn({
          x: tailX, y: tailY,
          vx: -dirX * (100 + Math.random() * 200) + (Math.random() - 0.5) * 120,
          vy: -dirY * (100 + Math.random() * 200) + (Math.random() - 0.5) * 120,
          life: 0.15, max: 0.15, size: 1.6,
          color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
        });
      }
      this.lights.push({ x: tailX, y: tailY, r: 34, k: 0.5, life: 0.03, max: 0.03 });
      let boomT = -1;
      const consider = (t: number) => {
        if (t >= 0 && (boomT < 0 || t < boomT)) boomT = t;
      };
      const hit = r.ghost ? { t: -1, index: -1 } : this.fight.intercept(r.x, r.y, x1, y1);
      consider(hit.t);
      if (x1 < 0 || x1 > this.world.W || y1 > this.world.H || y1 < -400) consider(1);
      for (const slab of this.chunks.list) consider(slabAlong(slab, r.x, r.y, x1, y1));
      if (r.range - step <= 0.001) consider(1);
      if (boomT >= 0) {
        let direct = -1;
        if (hit.index >= 0 && hit.t >= 0 && hit.t <= boomT + 0.0001) {
          direct = hit.index;
          boomT = hit.t;
        }
        const boomAt = boomT * step;
        if (!r.ghost) this.explode(r.x + dirX * boomAt, r.y + dirY * boomAt, ROCKET_R, 1, "rocket", direct);
        this.rockets.splice(i, 1);
      } else {
        r.x = x1;
        r.y = y1;
        r.range -= step;
        r.dist += step;
      }
    }
    for (let i = this.nades.length - 1; i >= 0; i--) {
      const g = this.nades[i];
      g.fuse -= dt;
      if (g.fuse <= 0) {
        if (!g.ghost) {
          const near = this.fight.nearestBody(g.x, g.y, GRENADE_R);
          this.explode(g.x, g.y, GRENADE_R, 1, "grenade", near);
        }
        this.nades.splice(i, 1);
        continue;
      }
      g.vy += 1900 * dt;
      g.vx -= g.vx * (g.grounded ? 4 : 0.2) * dt;
      const nx = g.x + g.vx * dt;
      const ny = g.y + g.vy * dt;
      if (!g.ghost) {
        const hit = this.fight.intercept(g.x, g.y, nx, ny, GRENADE_R);
        let direct = hit.index;
        if (direct < 0) direct = this.fight.nearestBody(g.x, g.y, GRENADE_R);
        if (direct < 0) direct = this.fight.nearestBody(nx, ny, GRENADE_R);
        if (direct >= 0) {
          const t = hit.index >= 0 && hit.t >= 0 ? hit.t : (this.fight.nearestBody(g.x, g.y, GRENADE_R) >= 0 ? 0 : 1);
          this.explode(g.x + (nx - g.x) * t, g.y + (ny - g.y) * t, GRENADE_R, 1, "grenade", direct);
          this.nades.splice(i, 1);
          continue;
        }
      }
      g.a += g.av * dt;
      if (nx < 4 || nx > this.world.W - 4) {
        g.vx = -g.vx * 0.5;
        g.av *= -0.7;
      } else g.x = nx;
      g.grounded = false;
      if (g.vy > 0) {
        const ground = this.world.findSurface(g.x - 2, g.x + 2, g.y + 3, ny + 4);
        if (!Number.isNaN(ground)) {
          if (!g.ghost) {
            const near = this.fight.nearestBody(g.x, ground - 4, GRENADE_R);
            if (near >= 0) {
              this.explode(g.x, ground - 4, GRENADE_R, 1, "grenade", near);
              this.nades.splice(i, 1);
              continue;
            }
          }
          g.y = ground - 4;
          if (g.vy > 160) {
            g.vy = -g.vy * 0.42;
            g.vx *= 0.8;
            g.av = g.vx * 0.12;
          } else {
            g.vy = 0;
            g.grounded = true;
            g.av = g.vx * 0.2;
          }
          continue;
        }
      }
      g.y = ny;
      if (g.y > this.world.H + 100) this.nades.splice(i, 1);
    }
    for (let i = this.mirvs.length - 1; i >= 0; i--) {
      const m = this.mirvs[i];
      m.t += dt;
      if (m.state === 0) {
        m.vy += 1178 * dt;
        m.a = Math.atan2(m.vy, m.vx);
        if (Math.random() < 0.6) {
          this.parts.smoke(m.x, m.y, -m.vx * 0.1, -m.vy * 0.1, (1.2 + Math.random() * 0.8) * CELL, 0.4 + Math.random() * 0.4, "#5b5566");
        }
        const nx = m.x + m.vx * dt;
        const ny = m.y + m.vy * dt;
        let struck = false;
        if (!m.ghost) {
          const t = this.fight.obstruct(m.x, m.y, nx, ny);
          if (t >= 0) {
            struck = true;
            m.x = m.x + (nx - m.x) * t;
            m.y = m.y + (ny - m.y) * t;
          }
        }
        if (!struck) {
          if (nx < 8 || nx > this.world.W - 8) m.vx *= -0.5;
          else m.x = nx;
          if (m.vy > 0) {
            const ground = this.world.findSurface(m.x - 4, m.x + 4, m.y + 6, ny + 6);
            if (!Number.isNaN(ground)) {
              m.y = ground - 6;
              m.vy = -Math.abs(m.vy) * 0.45;
              m.vx *= 0.7;
              m.bounces++;
            } else m.y = ny;
          } else m.y = ny;
        }
        if (m.state === 0 && ((m.t > 0.42 && m.vy > 30) || m.bounces > 0 || struck)) {
          m.state = 1;
          m.ct = 0;
          this.sfx("chutePop");
          this.rings.push({ x: m.x, y: m.y - 28, r0: 4, r1: 34, life: 0.22, max: 0.22, thick: 1.2, color: WHITE });
        }
        if (m.y > this.world.H + 200) this.mirvs.splice(i, 1);
        continue;
      }
      m.ct += dt;
      m.vy += (62 - m.vy) * Math.min(1, dt * 4.5);
      m.vx -= m.vx * 1.8 * dt;
      const dropX = m.x;
      const dropY = m.y;
      m.x += (m.vx + Math.cos(m.ct * 2.4) * 22) * dt;
      m.y += m.vy * dt;
      m.a += (Math.PI / 2 + Math.sin(m.ct * 2.4) * 0.3 - m.a) * Math.min(1, dt * 7);
      const struck = !m.ghost && this.fight.obstruct(dropX, dropY, m.x, m.y) >= 0;
      const ground = this.world.findSurface(m.x - 6, m.x + 6, m.y, m.y + 95);
      if (struck || m.ct > 1.8 || (m.ct > 0.55 && !Number.isNaN(ground)) || m.y > this.world.H + 200) {
        this.splitFx(m.x, m.y);
        this.chutes.push({
          x: m.x, y: m.y - 22,
          vx: m.vx * 0.5 + (Math.random() * 80 - 40),
          vy: -90, t: 0,
        });
        for (let k = 0; k < 8; k++) {
          const spread = (k + 0.5) / 8 - 0.5;
          this.bomblets.push({
            x: m.x, y: m.y + 4, vx: spread * 620 + (Math.random() - 0.5) * 120 + m.vx * 0.3,
            vy: -40 - Math.random() * 200, a: Math.random() * Math.PI * 2, av: (Math.random() - 0.5) * 28,
            fuse: 1.3 + Math.random() * 0.6, t: 0, bounced: false, ghost: m.ghost,
          });
        }
        this.mirvs.splice(i, 1);
      }
    }
    for (let i = this.bomblets.length - 1; i >= 0; i--) {
      const b = this.bomblets[i];
      b.t += dt;
      b.fuse -= dt;
      if (Math.random() < 0.8) {
        const ux = Math.cos(b.a);
        const uy = Math.sin(b.a);
        const life = 0.05 + Math.random() * 0.05;
        this.parts.spawn({
          x: b.x - ux * 14, y: b.y - uy * 14,
          vx: -ux * 120 + (Math.random() - 0.5) * 60,
          vy: -uy * 120 + (Math.random() - 0.5) * 60,
          life, max: life, size: (0.8 + Math.random() * 0.5) * CELL,
          color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "fireball",
        });
      }
      b.vy += GRAVITY * dt;
      b.vx -= b.vx * 0.3 * dt;
      b.a = Math.atan2(b.vy, b.vx);
      const nx = b.x + b.vx * dt;
      const ny = b.y + b.vy * dt;
      if (!b.ghost) {
        const hit = this.fight.intercept(b.x, b.y, nx, ny);
        if (hit.t >= 0) {
          this.explode(b.x + (nx - b.x) * hit.t, b.y + (ny - b.y) * hit.t, BOMBLET_R, 0.7, "overkill", hit.index);
          this.bomblets.splice(i, 1);
          continue;
        }
      }
      if (nx < 4 || nx > this.world.W - 4) b.vx *= -0.5;
      else b.x = nx;
      if (b.fuse <= 0) {
        if (!b.ghost) this.explode(b.x, b.y, BOMBLET_R, 0.7, "overkill");
        this.bomblets.splice(i, 1);
        continue;
      }
      if (b.vy > 0 && b.t > 0.12) {
        const ground = this.world.findSurface(b.x - 2, b.x + 2, b.y + 5, ny + 5);
        if (!Number.isNaN(ground)) {
          if (!b.bounced && Math.random() < 0.35) {
            b.bounced = true;
            b.y = ground - 5;
            b.vy *= -0.4;
            b.vx *= 0.7;
            b.av *= -0.6;
            continue;
          }
          if (!b.ghost) this.explode(b.x, ground - 3, BOMBLET_R, 0.7, "overkill");
          this.bomblets.splice(i, 1);
          continue;
        }
      }
      b.y = ny;
      if (b.y > this.world.H + 100) this.bomblets.splice(i, 1);
    }
    for (let i = this.chutes.length - 1; i >= 0; i--) {
      const z = this.chutes[i];
      z.t += dt;
      z.vy += (28 - z.vy) * Math.min(1, dt * 1.6);
      z.vx -= z.vx * 0.8 * dt;
      z.x += (z.vx + Math.sin(z.t * 3.1) * 26) * dt;
      z.y += z.vy * dt;
      if (z.t > 2.4) this.chutes.splice(i, 1);
    }
    this.edgeTint = 0;
    stepOrdnance(this.coils, this.bolts, this.meteors, dt, this.ordnanceHost());
    for (let i = this.beams.length - 1; i >= 0; i--) {
      this.beams[i].life -= dt;
      if (this.beams[i].life <= 0) this.beams.splice(i, 1);
    }
    for (let i = this.heats.length - 1; i >= 0; i--) {
      const heat = this.heats[i];
      heat.t += dt;
      if (heat.t >= heat.dur) {
        this.heats.splice(i, 1);
        continue;
      }
      const hot = heat.hot * Math.pow(1 - heat.t / heat.dur, 1.4);
      heat.acc += dt * Math.min(40, heat.cells.length / 18) * hot;
      const gw = this.world.GW;
      while (heat.acc > 1 && heat.cells.length) {
        heat.acc -= 1;
        const cell = heat.cells[(Math.random() * heat.cells.length) | 0];
        const x = ((cell % gw) + 0.5) * CELL;
        const y = (((cell / gw) | 0) + 0.5) * CELL;
        if (Math.random() < 0.55) {
          this.parts.spawn({
            x, y,
            vx: (Math.random() - 0.5) * 160,
            vy: -140 - Math.random() * 80,
            life: 0.1 + Math.random() * 0.2, max: 0.3, size: 1.4,
            color: "#ff8a2a", grav: 40, rubble: false, glow: true, kind: "spark",
          });
        } else {
          this.parts.smoke(x, y - 2, (Math.random() - 0.5) * 16, -25 - Math.random() * 10, (1.2 + Math.random() * 1.0) * CELL, 0.7 + Math.random() * 0.7, "");
        }
      }
    }
    for (let i = this.blazes.length - 1; i >= 0; i--) {
      const blaze = this.blazes[i];
      blaze.life -= dt;
      if (blaze.life <= 0 || this.world.matAt(blaze.x, blaze.y + 1) === 0) {
        this.blazes.splice(i, 1);
        continue;
      }
      const fade = blaze.life / blaze.max;
      const power = blaze.power * (fade > 0.7 ? 1 : 0.35 + (fade / 0.7) * 0.65);
      blaze.acc += dt * (6 + 16 * power);
      while (blaze.acc > 1) {
        blaze.acc -= 1;
        const size = (1.2 + Math.random() * 1.6) * power;
        const life = 0.28 + Math.random() * 0.32;
        this.parts.spawn({
          x: blaze.x + (Math.random() - 0.5) * 6, y: blaze.y - size * 0.6,
          vx: (Math.random() - 0.5) * 24, vy: -25 - Math.random() * 35,
          life, max: life, size: size * CELL,
          color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball",
          puff: Math.random() < 0.3 ? 1 : 0,
        });
        if (Math.random() < 0.06) {
          const ember = 0.6 + Math.random() * 0.8;
          this.parts.spawn({
            x: blaze.x, y: blaze.y - 3, vx: (Math.random() - 0.5) * 60, vy: -30 - Math.random() * 60,
            life: ember, max: ember, size: 1, color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
          });
        }
      }
    }
    if (this.blazes.length > 320) this.blazes.splice(0, this.blazes.length - 320);
    for (let i = this.lights.length - 1; i >= 0; i--) {
      this.lights[i].life -= dt;
      if (this.lights[i].life <= 0) this.lights.splice(i, 1);
    }
    for (const ring of this.rings) ring.life -= dt;
    this.rings = this.rings.filter((ring) => ring.life > 0);
    for (const flash of this.flashes) flash.life -= dt;
    this.flashes = this.flashes.filter((flash) => flash.life > 0);
    for (let i = this.pops.length - 1; i >= 0; i--) {
      if ((this.pops[i].t -= dt) > 0) continue;
      const pop = this.pops[i];
      this.pops.splice(i, 1);
      this.popBurst(pop.x, pop.y, pop.s);
    }
    this.screenFlash = Math.max(0, this.screenFlash - dt * 7);
    this.pain = Math.max(0, this.pain - dt * 2.6);
    this.hurtBlink = Math.max(0, this.hurtBlink - dt);
    this.shield = Math.max(0, this.shield - dt);
    if (this.y > this.world.H + 300) this.hurt(100, -1, "fall");
  }

  private puffFire(x: number, y: number, kind: "flame" | "soot") {
    if (kind === "soot") {
      this.parts.smoke(x, y, (Math.random() - 0.5) * 40, -18 - Math.random() * 28, (1.3 + Math.random() * 1.1) * CELL, 1 + Math.random() * 0.9, Math.random() < 0.5 ? "#2a241c" : "#1a1410");
      return;
    }
    const life = 0.22 + Math.random() * 0.23;
    this.parts.spawn({
      x, y,
      vx: (Math.random() * 2 - 1) * 10,
      vy: -(25 + Math.random() * 30),
      life, max: life,
      size: (0.9 + Math.random() * 0.9) * CELL,
      color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball",
      puff: Math.random() < 0.35 ? 1 : 0,
    });
    if (Math.random() < 0.12) {
      const ember = 0.5 + Math.random() * 0.7;
      this.parts.spawn({
        x, y, vx: (Math.random() * 2 - 1) * 25, vy: -(25 + Math.random() * 45),
        life: ember, max: ember, size: 1.2,
        color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
      });
    }
  }

  private after(dt: number) {
    this.letters.step(dt);
    this.chunks.update(dt, (slab, x, y, speed) => this.chunkImpact(slab, x, y, speed));
    this.fire.step(dt, this.world, (x, y, kind) => this.puffFire(x, y, kind));
    this.flushBurns(dt);
    this.parts.step(dt, this.world, this.fire);
    this.scorchLetters();
    this.camera(dt);
  }

  /** Burned cells damage their block, and a flame stream ignites the glyphs it crosses. */
  private flushBurns(dt: number) {
    for (const owner of this.fire.burned) this.burnDmg.set(owner, (this.burnDmg.get(owner) ?? 0) + 1);
    this.fire.burned.length = 0;
    this.burnAcc += dt;
    if (this.burnAcc <= 0.25) return;
    this.burnAcc = 0;
    for (const [owner, count] of this.burnDmg) this.world.damageBlock(owner, count * 1.3, 0, -1);
    this.burnDmg.clear();
  }

  private scorchLetters() {
    const list = this.parts.list;
    if (!list.length) return;
    const frame = Math.round(this.t * 60);
    let buckets: Set<number> | null = null;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      if (p.kind !== "flame") continue;
      const age = 1 - p.life / Math.max(1e-4, p.max);
      if (age < 0.02) continue;
      if ((i + frame) % 3 === 0) {
        const reach = 3 + age * 7;
        this.letters.query(p.x - reach, p.y - reach, p.x + reach, p.y + reach, (index) => {
          if (Math.random() < 0.55) this.letters.ignite(index, 3);
        });
      }
      (buckets ??= new Set()).add((Math.floor(p.y / 16) << 12) | (Math.floor(p.x / 16) & 0xfff));
    }
    if (!buckets) return;
    for (const body of this.letters.bodies) {
      if (body.burn >= 0) continue;
      const key = (Math.floor(body.y / 16) << 12) | (Math.floor(body.x / 16) & 0xfff);
      if (buckets.has(key)) body.burn = 0;
    }
  }

  explode(x: number, y: number, radius: number, power: number, kind: string, direct = -1) {
    const world = this.world;
    const owners = new Set<number>();
    world.beginCarve();
    world.log?.ctx(1, x, y, radius, power);
    this.chunks.spall(x, y, radius, Math.round((7 + Math.random() * 4) * power), 5, 12);
    world.carve(x, y, radius, 0.16, (_cx, _cy, _color, _mat, owner) => {
      if (owner >= 0) owners.add(owner);
    });
    world.scorch(x, y, radius * 0.92, radius * 1.7, 1);
    const blastRim = world.rim(3);
    this.heatRim(blastRim, 2.8, 0.92);
    this.seedRims(x, y, radius, Math.round(14 * power));
    world.log?.heat(3, 2.8, 0.92, false);
    const reach = radius * 1.9;
    this.letters.query(x - reach, y - reach, x + reach, y + reach, (i) => {
      const gx = this.letters.x[i] + this.letters.w[i] / 2;
      const gy = this.letters.y[i] + this.letters.h[i] / 2;
      const dx = gx - x;
      const dy = gy - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > reach) return;
      const fall = dist / reach;
      const impulse = 1050 * Math.pow(1 - fall, 0.7) + 110;
      this.fling(i, (dx / dist) * impulse + (Math.random() - 0.5) * 120, (dy / dist) * impulse - (120 + Math.random() * 200), (Math.random() - 0.5) * 56, dist < radius * 1.05 ? Math.random() * 0.25 : -1);
    });
    for (const body of this.letters.bodies) {
      const dx = body.x - x;
      const dy = body.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > radius * 2.3) continue;
      const push = 900 * (1 - dist / (radius * 2.3));
      body.vx += (dx / dist) * push;
      body.vy += (dy / dist) * push - 150;
      body.av += (Math.random() - 0.5) * 40;
      if (dist < radius && body.burn < 0) body.burn = 0.1;
    }
    for (const slab of this.chunks.list.slice()) {
      const dx = slab.x - x;
      const dy = slab.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      const limit = radius * 1.4 + Math.max(slab.bw, slab.bh) * 0.5;
      if (dist > limit) continue;
      if (!slab.fragment) this.chunks.shatter(slab, x, y, 420);
      else {
        const push = 700 * (1 - dist / limit);
        slab.vx += (dx / dist) * push;
        slab.vy += (dy / dist) * push - 200;
        slab.av += (Math.random() - 0.5) * 20;
      }
    }
    for (const owner of owners) world.damageBlock(owner, 150, 0, -1, x, y);
    world.blocks.forEach((block, index) => {
      if (!block.alive || owners.has(index)) return;
      const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
      const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      if (dist < radius * 1.8) world.damageBlock(index, 90 * (1 - dist / (radius * 1.8)), 0, -1, x, y);
    });
    this.fire.ignite(world, x, y - 4);
    if (this.selfBlast(x, y, radius * 2.6, 1350, 0.4)) this.trauma = Math.min(1, this.trauma + 0.62 * power);
    this.fight.hurt(x, y, radius * 1.15, minionBlastDmg(kind), direct);
    if (this.online) {
      const dmg = PLAYER_BLAST_DMG[kind] ?? 40;
      for (const remote of this.remotes) {
        if (!remote.alive) continue;
        const rd = Math.hypot(remote.x - x, remote.y - 28 - y);
        if (rd < radius) this.onShot?.("hitp", { to: remote.id, dmg: dmg * (1 - rd / radius), w: kind });
      }
    }
    this.flashes.push({ x, y, r: (radius / CELL) * 1.05, life: 0.06, max: 0.06 });
    this.flashScreen(Math.min(1, this.screenFlash + 0.85 * power), "255,246,222");
    this.rings.push({ x, y, r0: radius * 0.45, r1: radius * 2.7, life: 0.34, max: 0.34, thick: 3, color: YELLOW });
    this.rings.push({ x, y, r0: radius * 0.3, r1: radius * 1.6, life: 0.22, max: 0.22, thick: 1.8, color: ORANGE });
    this.lights.push({ x, y, r: radius * 3.4, k: 0.6 * power, life: 0.7, max: 0.7 });
    this.lights.push({ x, y, r: radius * 1.6, k: 0.6, life: 0.08, max: 0.08 });
    const cells = radius / CELL;
    const fireball = (px: number, py: number, vx: number, vy: number, life: number, sizeCells: number, puff: 0 | 1 | 2 = 0) => {
      this.parts.spawn({
        x: px, y: py, vx, vy, life, max: life,
        size: sizeCells * CELL,
        color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff,
      });
    };
    for (let n = 0; n < 5; n++) {
      fireball(x + (Math.random() - 0.5) * 10, y + (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 80, -20 - Math.random() * 60, 0.18 + Math.random() * 0.12, cells * (0.4 + Math.random() * 0.15));
    }
    for (let n = 0; n < Math.round(28 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.55;
      const speed = (80 + Math.random() * 340) * power;
      fireball(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * speed, Math.sin(ang) * speed * 0.8 - 80,
        0.55 + Math.random() * 0.6, cells * (0.22 + Math.random() * 0.24),
        Math.random() < 0.45 ? 2 : 0,
      );
    }
    for (let n = 0; n < 5; n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.1 + Math.random() * 0.4);
      this.parts.smoke(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * 60, Math.sin(ang) * 40 - 60,
        cells * (0.15 + Math.random() * 0.11) * CELL, 0.9 + Math.random() * 0.6, "#221d26",
      );
    }
    for (let n = 0; n < 5; n++) {
      fireball(x + (Math.random() - 0.5) * radius * 0.5, y, (Math.random() - 0.5) * 50, -200 - Math.random() * 160, 0.8 + Math.random() * 0.5, cells * (0.24 + Math.random() * 0.12), Math.random() < 0.6 ? 2 : 0);
    }
    for (let n = 0; n < Math.round(80 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = (320 + Math.random() * 880) * power;
      const life = 0.18 + Math.random() * 0.52;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 140,
        life, max: life, size: 1.2,
        color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
    for (let n = 0; n < Math.round(40 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 300;
      const life = 1.2 + Math.random() * 2;
      this.parts.spawn({
        x: x + Math.cos(ang) * radius * 0.4, y: y + Math.sin(ang) * radius * 0.4,
        vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 150,
        life, max: life, size: 1,
        color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
      });
    }
    const chunks = Math.round((9 + Math.random() * 5) * power);
    for (let n = 0; n < chunks; n++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.7;
      const speed = 380 + Math.random() * 440;
      const life = 0.7 + Math.random() * 0.8;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed,
        life, max: life, size: 3.2,
        color: "#ffe26b", grav: GRAVITY * 0.8, rubble: false, glow: true, kind: "chunk",
      });
    }
    for (let n = 0; n < Math.round(10 * power); n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.1 + Math.random() * 0.6);
      const life = 1.8 + Math.random() * 1.2;
      this.parts.smoke(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * (30 + Math.random() * 170), Math.sin(ang) * (20 + Math.random() * 100) - 70,
        cells * (0.26 + Math.random() * 0.18) * CELL, life,
      );
    }
    for (let n = 0; n < 3; n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = radius * (0.4 + Math.random() * 0.7);
      this.pops.push({
        x: x + Math.cos(ang) * dist,
        y: y + Math.sin(ang) * dist,
        t: 0.07 + Math.random() * 0.27,
        s: 0.45 + Math.random() * 0.25,
      });
    }
    this.explosions++;
    this.sfx(radius > 40 ? "boom" : "boomSmall");
    this.onShot?.("boom", { x, y, r: radius, k: kind });
    void DT;
  }

  /** Delayed secondary bursts. They are what fills the orange cloud a moment after Too Much splits. */
  private popBurst(x: number, y: number, s: number) {
    const cells = 26 * s / CELL;
    this.flashes.push({ x, y, r: cells * 0.8, life: 0.04, max: 0.04 });
    this.lights.push({ x, y, r: 90 * s, k: 0.55, life: 0.25, max: 0.25, color: hex("#ffb060") });
    for (let n = 0; n < 6; n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 160;
      const life = 0.35 + Math.random() * 0.35;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 50,
        life, max: life, size: cells * (0.35 + Math.random() * 0.25) * CELL,
        color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff: 1,
      });
    }
    for (let n = 0; n < 14; n++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 250 + Math.random() * 450;
      const life = 0.12 + Math.random() * 0.28;
      this.parts.spawn({
        x, y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 80,
        life, max: life, size: 1.2,
        color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
  }

  /** A flying slab hits the page: it knocks glyphs loose, and a heavy intact piece breaks. */
  private chunkImpact(slab: Slab, x: number, y: number, speed: number) {
    const span = Math.sqrt(slab.bw * slab.bh);
    const reach = Math.min(60, span * 0.35 + 8);
    if (!slab.ghost) {
      this.letters.query(x - reach, y - reach * 0.6, x + reach, y + reach * 0.6, (index) => {
        if (Math.random() >= 0.6) return;
        const vx = (Math.random() - 0.5) * 320;
        const vy = -(120 + Math.random() * 240);
        const av = (Math.random() - 0.5) * 40;
        this.letters.detach(index, vx, vy, -1, false, { vx, vy, av, burn: -1, pinned: false });
      });
    }
    if (!slab.fragment && speed > 300) {
      this.chunks.shatter(slab, x, y, Math.min(420, speed * 0.55));
      this.sfx("shatter");
      return;
    }
    if (speed > 200) {
      this.sfx("hitChunk");
      if (span > 80) this.trauma = Math.min(1, this.trauma + Math.min(0.06, span / 5000));
      return;
    }
    this.sfx("hitChunk");
  }

  private fling(index: number, vx: number, vy: number, av: number, burn: number) {
    this.letters.detach(index, vx, vy, burn, false, { vx, vy, av, burn, pinned: false });
  }

  private bulletImpact(bullet: Bullet, dirX: number, dirY: number) {
    const world = this.world;
    const ownerAt = world.owner[Math.floor(bullet.y / CELL) * world.GW + Math.floor(bullet.x / CELL)] ?? -1;
    const image = ownerAt >= 0 && world.blocks[ownerAt]?.kind === "image";
    this.sfx(image ? "hitImage" : "hitBlock");
    const radius = (bullet.heavy ? 5.5 : bullet.weapon === "pistol" ? 8.5 : bullet.weapon === "drone" ? 8.5 : 6.5) * (image ? 1.35 : 1);
    const owners = new Set<number>();
    const chips = image ? 1 + Math.floor(Math.random() * 3) : Math.random() < 0.45 ? 1 + Math.floor(Math.random() * 2) : 0;
    if (chips) this.chunks.spall(bullet.x + dirX * radius * 0.6, bullet.y + dirY * radius * 0.6, radius * 1.2, chips, image ? 4 : 3.5, image ? 11 : 7);
    world.beginCarve();
    world.log?.ctx(2, bullet.x, bullet.y, dirX, dirY);
    let debris = 0;
    const note = (cx: number, cy: number, color: number, _mat: number, owner: number) => {
      if (owner >= 0) owners.add(owner);
      if (debris >= (bullet.heavy ? 6 : 3) || Math.random() >= 0.92) return;
      debris++;
      const side = Math.random() < 0.5 ? -1 : 1;
      this.parts.chip(
        (cx + 0.5) * CELL,
        (cy + 0.5) * CELL,
        dirX * (40 + Math.random() * 160) - dirY * side * (30 + Math.random() * 90),
        dirY * (20 + Math.random() * 80) - (40 + Math.random() * 120),
        color,
        0.55,
      );
    };
    world.carve(bullet.x, bullet.y, radius, 0.42, note);
    for (const [along, scale] of [[0.9, 0.72], [1.75, 0.5]] as const) {
      const x = bullet.x + dirX * radius * along;
      const y = bullet.y + dirY * radius * along;
      if (world.matAt(x, y) >= SOLID) world.carve(x, y, radius * scale, 0.5, note);
    }
    world.scorch(bullet.x, bullet.y, radius, radius + 3, 0.6);
    const rim = world.rim(1);
    world.char(rim.cells, rim.ring);
    this.heatRim(rim, 0.55, 0.62);
    world.log?.heat(1, 0.55, 0.62, false);
    for (const owner of owners) world.damageBlock(owner, bullet.dmg, dirX, dirY, undefined, undefined, bullet.x, bullet.y);
    const scale = bullet.heavy ? 0.8 : bullet.weapon === "drone" ? 1.3 : 1;
    const sparks = Math.round(4 + scale * 4);
    for (let i = 0; i < sparks; i++) {
      const ang = Math.atan2(-dirY, -dirX) + (Math.random() - 0.5) * 2.4;
      const speed = 180 + Math.random() * 460;
      const life = 0.08 + Math.random() * 0.24;
      this.parts.spawn({
        x: bullet.x, y: bullet.y, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 60,
        life, max: life, size: 1.2,
        color: "#ff8a2a", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
    this.lights.push({ x: bullet.x, y: bullet.y, r: 26, k: 0.5, life: 0.08, max: 0.08 });
  }

  private paperHole(x: number, y: number, dirX: number, dirY: number, radius: number) {
    this.world.beginCarve();
    this.world.log?.ctx(3, x, y, dirX, dirY);
    this.world.carve(x, y, radius, 0.3);
    this.world.scorch(x, y, radius, radius + 2.4, 0.8);
    this.sfx("hitPaper");
    this.parts.burst(x, y, this.world.colorAt(x, y), 4, 160, true);
  }

  hurt(amount: number, by: number, weapon: string) {
    if (!this.alive || this.shield > 0) return;
    this.hp -= amount;
    this.sfx("hitBlock");
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
      this.vy = -300;
      this.sfx("shatter");
      this.onShot?.("die", { x: this.x, y: this.y, by, w: weapon });
      if (this.downed) return;
      setTimeout(() => {
        if (!this.alive) this.respawn();
      }, 1400);
    }
  }

  /** Journey monster hit. Shield and the fight's grace window keep it from stacking. */
  bite(amount: number): boolean {
    if (!this.downed || !this.alive || this.shield > 0 || amount <= 0) return false;
    this.hp = Math.max(0, this.hp - amount);
    this.pain = 1;
    this.hurtBlink = 1.2;
    this.sfx("hurt");
    if (this.hp <= 0) {
      this.alive = false;
      this.vy = -300;
      this.sfx("shatter");
      this.onShot?.("die", { x: this.x, y: this.y, by: -1, w: "bite" });
    }
    return true;
  }

  /** Journey heart. Full health leaves the pickup on the ground. */
  heal(amount: number): boolean {
    if (!this.downed || !this.alive || this.hp >= 100 || amount <= 0) return false;
    this.hp = Math.min(100, this.hp + amount);
    this.sfx("heal");
    return true;
  }

  private snapCam(x: number, y: number) {
    const [cx, cy] = clampCam(x - this.viewW / 2, y - this.viewH / 2, this.viewW, this.viewH, this.world.W, this.world.H, this.camTop);
    this.camX = cx;
    this.camY = cy;
  }

  camera(dt: number) {
    const focusX = this.drone ? this.drone.x : this.x;
    const focusY = this.drone ? this.drone.y + 16 : this.y - 40;
    this.camTop = Math.min(-40, Math.max(-460, (this.drone ? this.drone.y - 30 : this.y) - 58 - 120));
    if (this.intro < 0.05) {
      this.snapCam(this.x, this.spawnY - 100);
    } else {
      const aimX = focusX + Math.max(-140, Math.min(140, (this.lookX - focusX) * 0.18));
      const aimY = focusY + Math.max(-90, Math.min(90, (this.lookY - focusY) * 0.14));
      const [tx, ty] = clampCam(aimX - this.viewW / 2, aimY - this.viewH / 2, this.viewW, this.viewH, this.world.W, this.world.H, this.camTop);
      this.camX += (tx - this.camX) * (1 - Math.exp(-dt * 7));
      this.camY += (ty - this.camY) * (1 - Math.exp(-dt * 5.5));
    }
    this.trauma *= Math.exp(-dt * 1.7);
    this.shake = Math.max(0, this.shake - dt * 3);
  }

  camShake(): { x: number; y: number } {
    const m = (this.trauma + this.shake * 0.2) * 14;
    return { x: (Math.random() - 0.5) * m, y: (Math.random() - 0.5) * m };
  }

  /** Kept so older callers compile. Slabs detach themselves through world.breaker. */
  absorb(_piece: { canvas: HTMLCanvasElement; x: number; y: number; w: number; h: number; av: number } | null, _vx: number, _vy: number, _ghost = false) {}

  private heatRim(rim: { cells: number[]; ring: number[] }, dur: number, hot: number) {
    if (!rim.cells.length) return;
    const last = this.heats[this.heats.length - 1];
    if (last && last.t < 0.1 && last.dur === dur && last.hot === hot && last.cells.length < 24000) {
      const cells = new Int32Array(last.cells.length + rim.cells.length);
      const ring = new Uint8Array(cells.length);
      cells.set(last.cells);
      cells.set(rim.cells, last.cells.length);
      ring.set(last.ring);
      ring.set(rim.ring, last.ring.length);
      last.cells = cells;
      last.ring = ring;
      return;
    }
    this.heats.push({ cells: Int32Array.from(rim.cells), ring: Uint8Array.from(rim.ring), t: 0, dur, hot, acc: 0 });
    if (this.heats.length > 48) this.heats.shift();
  }

  /** Ground fires around a blast. They keep feeding the orange ring after the fireball fades. */
  private seedRims(x: number, y: number, radius: number, count: number) {
    const world = this.world;
    let left = count;
    for (let n = 0; n < count * 3 && left > 0; n++) {
      const ang = Math.random() * Math.PI * 2;
      for (let dist = radius * 0.8; dist < radius * 1.6; dist += CELL) {
        const px = x + Math.cos(ang) * dist;
        const py = y + Math.sin(ang) * dist;
        if (world.matAt(px, py) !== 0) {
          const life = 3.5 + Math.random() * 4.5;
          this.blazes.push({ x: px, y: py, life, max: life, acc: Math.random(), power: 0.8 + Math.random() * 0.35 });
          left--;
          break;
        }
      }
    }
    for (let n = 0; n < 6; n++) {
      const px = x + (Math.random() - 0.5) * radius * 4;
      const sy = world.findSurface(px - 2, px + 2, y - radius * 1.2, y + radius * 1.4);
      if (Number.isNaN(sy)) continue;
      const life = 2.5 + Math.random() * 3.5;
      this.blazes.push({ x: px, y: sy - 0.5, life, max: life, acc: Math.random(), power: 0.6 + Math.random() * 0.4 });
    }
  }

  private viewBuf(kind: "heat" | "shade", camX: number, camY: number): PixBuf {
    const cols = Math.ceil(this.viewW / CELL) + 4;
    const rows = Math.ceil(this.viewH / CELL) + 4;
    const key = kind === "heat";
    let buf = key ? this.heatBuf : this.shadeBuf;
    if (!buf || buf.w !== cols || buf.h !== rows) {
      buf = new PixBuf(cols, rows);
      const canvas = document.createElement("canvas");
      canvas.width = cols;
      canvas.height = rows;
      if (key) {
        this.heatBuf = buf;
        this.heatCanvas = canvas;
        this.heatImage = null;
      } else {
        this.shadeBuf = buf;
        this.shadeCanvas = canvas;
        this.shadeImage = null;
      }
    }
    buf.ox = Math.floor(camX / CELL) - 1;
    buf.oy = Math.floor(camY / CELL) - 1;
    buf.clear(0);
    return buf;
  }

  private blitBuf(ctx: CanvasRenderingContext2D, buf: PixBuf, canvas: HTMLCanvasElement, image: ImageData | null, camX: number, camY: number, scale: number, composite: GlobalCompositeOperation): ImageData | null {
    // CPU-backed: these are rewritten every frame, so putImageData stays a memcpy and the draw uploads once.
    const g = canvas.getContext("2d", { willReadFrequently: true });
    if (!g) return image;
    if (!image || image.width !== buf.w || image.height !== buf.h) image = g.createImageData(buf.w, buf.h);
    image.data.set(buf.bytes);
    g.putImageData(image, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalCompositeOperation = composite;
    ctx.drawImage(canvas, (buf.ox * CELL - camX) * scale, (buf.oy * CELL - camY) * scale, buf.w * this.world.pix, buf.h * this.world.pix);
    ctx.restore();
    return image;
  }

  /** Darken empty cells beside standing ground, the same offset the original uses for a trench. */
  private drawHoleShadows(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    const world = this.world;
    if (world.removedCells <= 0 || !this.shadeCanvas) {
      if (world.removedCells <= 0) return;
    }
    const buf = this.viewBuf("shade", camX, camY);
    const canvas = this.shadeCanvas;
    if (!canvas) return;
    const gw = world.GW;
    const gh = world.GH;
    const mat = world.mat;
    const empty = (x: number, y: number) => x < 0 || y < 0 || x >= gw || y >= gh || mat[y * gw + x] === 0;
    const deep = hex("#080412", 150);
    const soft = hex("#080412", 90);
    for (let y = buf.oy; y < buf.oy + buf.h; y++) {
      if (y < -2 || y > gh + 2) continue;
      for (let x = buf.ox; x < buf.ox + buf.w; x++) {
        if (!empty(x, y)) continue;
        if (!empty(x - 1, y - 2)) buf.set(x, y, deep);
        else if (!empty(x - 2, y - 4) && bayer(x, y) < 0.5) buf.set(x, y, soft);
      }
    }
    this.shadeImage = this.blitBuf(ctx, buf, canvas, this.shadeImage, camX, camY, scale, "source-over");
  }

  private heatSample(cell: number, ring: number, hot: number): number {
    const dither = ((Math.imul(cell, 2654435761)) >>> 0) / 4294967296;
    return hot - (ring - 1) * 0.28 - dither * 0.14;
  }

  private drawHeat(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    const buf = this.viewBuf("heat", camX, camY);
    if (!this.heats.length) return;
    const world = this.world;
    const last = HEAT_COLORS.length - 1;
    const pix = world.pix;
    ctx.save();
    for (const heat of this.heats) {
      const hot = heat.hot * Math.pow(1 - heat.t / heat.dur, 1.4);
      for (let i = 0; i < heat.cells.length; i++) {
        const cell = heat.cells[i];
        if (world.mat[cell] === 0) continue;
        const x = cell % world.GW;
        const y = (cell / world.GW) | 0;
        const h = this.heatSample(cell, heat.ring[i], hot);
        if (h <= 0.02) continue;
        if (h < 0.16 && bayer(x, y) > h / 0.16) continue;
        const idx = Math.max(0, Math.min(last, Math.floor((1 - h) * last)));
        ctx.fillStyle = HEAT_COLORS[idx];
        ctx.fillRect((x * CELL - camX) * scale, (y * CELL - camY) * scale, pix, pix);
        if ((i & 7) === 0 && idx < 7) buf.glow(x + 0.5, y + 0.5, 5, HEAT_PACKED[idx], 0.2 * h);
      }
    }
    ctx.restore();
  }

  private drawHeatGlow(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    const buf = this.heatBuf;
    const canvas = this.heatCanvas;
    if (!buf || !canvas) return;
    this.heatImage = this.blitBuf(ctx, buf, canvas, this.heatImage, camX, camY, scale, "lighter");
  }

  private drawLights(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    if (!this.lights.length) return;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const light of this.lights) {
      if (light.color !== undefined) continue;
      const k = light.k * (light.life / light.max) ** 2;
      if (k < 0.02) continue;
      const x = (light.x - camX) * scale;
      const y = (light.y - camY) * scale;
      const rad = Math.max(2, light.r * scale);
      const grad = ctx.createRadialGradient(x, y, 0, x, y, rad);
      grad.addColorStop(0, `rgba(255,176,96,${k.toFixed(3)})`);
      grad.addColorStop(1, "rgba(255,176,96,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, rad, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  blockCensus() {
    const blocks = this.world.blocks;
    let detachable = 0;
    let soft = 0;
    for (const block of blocks) {
      if (!block.canDetach) continue;
      detachable++;
      if (block.hp < 130) soft++;
    }
    const top = blocks
      .map((block) => ({ x: Math.round(block.x), y: Math.round(block.y), w: Math.round(block.w), h: Math.round(block.h), hp: Math.round(block.hp), can: block.canDetach, kind: block.kind, alive: block.alive }))
      .sort((a, b) => b.w * b.h - a.w * a.h)
      .slice(0, 8);
    return { n: blocks.length, detachable, soft, top };
  }

  draw(ctx: CanvasRenderingContext2D, hold: Hold | null, jet: Sprite | null, jetPivot: number[] | null, sprites: Record<string, Sprite>, holdOf?: (sprite: string) => Hold | null) {
    const shake = this.camShake();
    const camX = this.camX + shake.x;
    const camY = this.camY + shake.y;
    const scale = this.world.Z;
    const viewW = this.viewW * scale;
    const viewH = this.viewH * scale;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, viewW, viewH);
    ctx.clip();
    ctx.imageSmoothingEnabled = false;
    const sx = camX * scale;
    const sy = camY * scale;
    this.world.pageCache.drawView(ctx, sx, sy, viewW, viewH);
    this.world.textCache.drawView(ctx, sx, sy, viewW, viewH);
    this.world.drawCracks(ctx, camX, camY, scale);
    this.drawHoleShadows(ctx, camX, camY, scale);
    this.drawHeat(ctx, camX, camY, scale);
    this.chunks.draw(ctx, camX, camY, scale);
    this.letters.draw(ctx, camX, camY, scale);
    this.fire.draw(ctx, this.world, camX, camY, scale);
    this.drawProjectiles(ctx, camX, camY, scale, sprites);
    ctx.save();
    ctx.setTransform(scale, 0, 0, scale, -camX * scale, -camY * scale);
    heroFx.grab = 0;
    heroFx.charge = 0;
    heroFx.magnet = 0;
    for (const remote of this.remotes) {
      if (!remote.alive) continue;
      const weapon = WEAPONS[remote.weapon] ?? WEAPONS[0];
      heroFx.magnet = weapon.kind === "magnet" || weapon.kind === "meteor" || weapon.kind === "tesla" ? 1 : 0;
      drawStick(ctx, {
        x: remote.x, y: remote.y, facing: remote.facing, aim: remote.aim,
        vx: remote.vx, vy: remote.vy, grounded: remote.grounded, phase: remote.x / 40, jet: remote.jet, flip: -1, tumble: remote.tumble, skin: remote.skin,
        dt: DT, t: this.t + remote.id * 0.001, who: `r${remote.id}`,
        holdDist: weapon.holdDist, holdDrop: weapon.holdDrop, twoHand: weapon.twoHanded, grasp: weapon.grasp,
        ground: (x, y) => this.world.groundNear(x, y),
      }, holdOf?.(weapon.sprite) ?? null, remote.jet > 0.02 ? sprites.jetpack ?? null : null, jetPivot);
    }
    this.fight.draw(ctx, { x: camX, y: camY, w: this.viewW, h: this.viewH });
    this.worldDraw?.(ctx);
    if (this.def.kind === "meteor" || this.def.kind === "tesla") heroFx.magnet = 1;
    heroFx.gem = 0;
    if (this.def.kind === "meteor" && this.held) {
      const tip = muzzleOf(this.aimPose(), this.held);
      heroFx.gem = this.gemFlash;
      heroFx.gx = tip.x;
      heroFx.gy = tip.y;
    }
    const blinkOff = this.hurtBlink > 0 && Math.floor(this.hurtBlink * 8) % 2 === 0;
    if (!blinkOff) drawStick(ctx, this.pose(), this.held ?? hold, jet, jetPivot);
    this.drill.draw(ctx);
    this.fight.drawFront(ctx);
    if (this.drone) this.drawDrone(ctx, sprites, this.drone);
    for (const remote of this.remotes) if (remote.drone) this.drawDrone(ctx, sprites, remote.drone);
    ctx.restore();
    this.drawFx(ctx, camX, camY, scale);
    this.drawLights(ctx, camX, camY, scale);
    this.drawHeatGlow(ctx, camX, camY, scale);
    if (this.fxBuf && this.fxCanvas) this.fxImage = this.blitBuf(ctx, this.fxBuf, this.fxCanvas, this.fxImage, camX, camY, scale, "source-over");
    this.parts.draw(ctx, camX, camY, scale);
    if (this.screenFlash > 0.02) {
      ctx.fillStyle = `rgba(${this.screenTint},${(Math.min(1, this.screenFlash) * 0.22).toFixed(3)})`;
      ctx.fillRect(0, 0, viewW, viewH);
    }
    this.edgeShown += (this.edgeTint - this.edgeShown) * 0.35;
    if (this.edgeShown > 0.02) {
      // Summoning tint: red creeping in from the screen edges, clear in the middle.
      const k = Math.min(1, this.edgeShown);
      const g = ctx.createRadialGradient(viewW / 2, viewH / 2, Math.min(viewW, viewH) * 0.32, viewW / 2, viewH / 2, Math.hypot(viewW, viewH) * 0.56);
      g.addColorStop(0, "rgba(160,20,10,0)");
      g.addColorStop(1, `rgba(170,24,12,${(0.34 * k).toFixed(3)})`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, viewW, viewH);
    }
    if (this.pain > 0.02) {
      ctx.fillStyle = `rgba(255, 36, 52, ${(Math.min(1, this.pain) * 0.42).toFixed(3)})`;
      ctx.fillRect(0, 0, viewW, viewH);
    }
    ctx.restore();
  }

  ghostEvent(kind: string, body: Record<string, unknown>) {
    if (kind === "rkt") {
      this.rockets.push({
        x: Number(body.x) || 0, y: Number(body.y) || 0, a: Number(body.a) || 0, speed: 700, t: 0,
        range: Number(body.r) || 400, dist: 0, weapon: "rocket", ghost: true, wobble: 0,
      });
    } else if (kind === "gren") {
      this.nades.push({
        x: Number(body.x) || 0, y: Number(body.y) || 0, vx: Number(body.vx) || 0, vy: Number(body.vy) || 0,
        a: 0, av: Number(body.av) || 0, fuse: 1.55, grounded: false, weapon: "grenade", ghost: true,
      });
    } else if (kind === "mirv") {
      this.mirvs.push({
        x: Number(body.x) || 0, y: Number(body.y) || 0, vx: Number(body.vx) || 200, vy: Number(body.vy) || -40,
        a: Number(body.a) || 0, state: 0, t: 0, ct: 0, bounces: 0, ghost: true,
      });
    } else if (kind === "drill") {
      this.drill.launch(Number(body.x) || 0, Number(body.y) || 0, Number(body.a) || 0, Number(body.vx) || 0, Number(body.vy) || 0, true);
    }
  }

  private splitFx(x: number, y: number) {
    this.sfx("split");
    this.flashes.push({ x, y, r: 8, life: 0.05, max: 0.05 });
    this.lights.push({ x, y, r: 120, k: 0.9, life: 0.2, max: 0.2, color: WHITE });
    this.rings.push({ x, y, r0: 4, r1: 40, life: 0.24, max: 0.24, thick: 1.6, color: YELLOW });
    for (let n = 0; n < 18; n++) {
      const a = Math.random() * Math.PI * 2;
      const s = 200 + Math.random() * 400;
      const life = 0.1 + Math.random() * 0.25;
      this.parts.spawn({
        x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
        life, max: life, size: 1.2, color: "#fff3a6", grav: 200, rubble: false, glow: true, kind: "spark",
      });
    }
    for (let n = 0; n < 6; n++) {
      this.parts.smoke(x, y, (Math.random() - 0.5) * 160, -20 + Math.random() * 40, (1.6 + Math.random()) * CELL, 0.6 + Math.random() * 0.5, "#5b5566");
    }
  }

  private drawDrone(ctx: CanvasRenderingContext2D, sprites: Record<string, Sprite>, d: { x: number; y: number; a: number; rotor: number; tilt?: number; face?: number; battery?: number; state?: number; age?: number; gunA?: number; barrel?: number; kick?: number } | null) {
    const spr = sprites.drone;
    if (!d || !spr) return;
    const frame = Math.floor(d.rotor) % spr.framePix.length;
    const face = d.face ?? (Math.cos(d.a) >= 0 ? 1 : -1);
    const tilt = d.tilt ?? 0;
    const angle = face > 0 ? tilt : tilt + Math.PI;
    const rot = spriteRot(spr, 23, 21, 128, frame);
    const spun = rot.get(angle, face < 0);
    const bob = d.state === 2 ? Math.sin((d.age ?? 0) * 4.3) * 1.3 : 0;
    ctx.imageSmoothingEnabled = false;
    drawRot(ctx, spun, d.x, d.y + bob, 2);
    const gun = sprites.gatling;
    if (gun) {
      const gunA = d.gunA ?? d.a;
      const flip = face < 0;
      const [adx, ady] = rot.anchor(23 - spr.trimX, 27 - spr.trimY, rot.quantize(angle), flip);
      const kick = d.kick ?? 0;
      const mountX = d.x + adx * CELL - Math.cos(gunA) * kick * CELL;
      const mountY = d.y + bob + ady * CELL - Math.sin(gunA) * kick * CELL;
      const frameN = Math.floor(d.barrel ?? 0) % gun.framePix.length;
      const grot = spriteRot(gun, 5, 12, 128, frameN);
      drawRot(ctx, grot.get(gunA, Math.cos(gunA) < 0), mountX, mountY, 2);
    }
    if (d.state !== 1 && d.state !== 2) return;
    const charge = (d.battery ?? 12) / 12;
    const filled = Math.max(0, Math.ceil(charge * 8));
    const ink = charge > 0.5 ? "#7cff6b" : charge > 0.25 ? "#ffd65a" : "#ff4d4d";
    const bx = Math.round(d.x / CELL) - 5;
    const by = Math.round((d.y + bob) / CELL) - 16;
    const cell = (x: number, y: number, color: string) => {
      ctx.fillStyle = color;
      ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
    };
    for (let w = -1; w <= 9; w++) {
      cell(bx + w, by - 1, "#1a1422");
      cell(bx + w, by + 3, "#1a1422");
    }
    for (let row = 0; row < 3; row++) {
      cell(bx - 1, by + row, "#1a1422");
      cell(bx + 9, by + row, "#1a1422");
    }
    cell(bx + 10, by + 1, "#1a1422");
    for (let w = 0; w < 9; w++) {
      for (let row = 0; row < 3; row++) {
        cell(bx + w, by + row, w < filled ? (row === 0 ? "#c8ffc2" : ink) : "#282234");
      }
    }
  }

  private drawProjectiles(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number, sprites: Record<string, Sprite>) {
    const blit = (sprite: Sprite | undefined, x: number, y: number, a: number, steps: number) => {
      if (!sprite) return;
      const ax = sprite.trimX + sprite.pix.w / 2;
      const ay = sprite.trimY + sprite.pix.h / 2;
      const frame = spriteRot(sprite, ax, ay, steps).get(a, false);
      const px = Math.round(x / 2) - frame.ox;
      const py = Math.round(y / 2) - frame.oy;
      ctx.drawImage(frame.canvas, (px * 2 - camX) * scale, (py * 2 - camY) * scale, frame.sprite.w * 2 * scale, frame.sprite.h * 2 * scale);
    };
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    for (const r of this.rockets) blit(sprites.rocket, r.x, r.y, r.a, 128);
    for (const g of this.nades) blit(sprites.grenade, g.x, g.y, g.a, 64);
    for (const m of this.mirvs) {
      if (m.state === 1 && sprites.parachute) {
        const open = Math.min(1, m.ct / 0.22);
        const swell = 1 + 2.2 * (open - 1) ** 3 + 1.2 * (open - 1) ** 2;
        const sway = Math.sin(m.ct * 2.4 - 0.5) * 0.22;
        const cord = 1 + swell * 3;
        const ax = m.x + Math.sin(sway) * cord * CELL;
        const ay = m.y - cord * CELL;
        ctx.strokeStyle = "#141018";
        ctx.lineWidth = Math.max(1, scale);
        ctx.beginPath();
        ctx.moveTo((m.x - camX) * scale, (m.y - camY) * scale);
        ctx.lineTo((ax - camX) * scale, (ay - camY) * scale);
        ctx.stroke();
        const img = sprites.parachute.img;
        ctx.save();
        ctx.translate((ax - camX) * scale, (ay - camY) * scale);
        ctx.rotate(sway);
        const unit = 2 * scale;
        ctx.drawImage(img, -15 * unit, -26 * unit, img.width * unit, img.height * unit);
        ctx.restore();
      }
      blit(sprites.mirv, m.x, m.y, m.a, 128);
    }
    if (sprites.parachute) {
      for (const chute of this.chutes) {
        const fade = Math.max(0, 1 - Math.max(0, chute.t - 1.4));
        if (fade <= 0.02) continue;
        const sway = Math.sin(chute.t * 3.1) * 0.35;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate((chute.x - camX) * scale, (chute.y - camY) * scale);
        ctx.rotate(sway);
        const unit = 2 * scale;
        const img = sprites.parachute.img;
        ctx.drawImage(img, -15 * unit, -26 * unit, img.width * unit, img.height * unit);
        ctx.restore();
      }
    }
    for (const b of this.bomblets) blit(sprites.bomblet, b.x, b.y, b.a, 64);
    ctx.restore();
  }

  /** Cell-buffer beams, ordnance, shock rings and tracers. */
  private drawFx(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    const buf = this.ensureFx(camX, camY);
    const light = this.heatBuf;
    if (!light || !this.fxCanvas) return;
    for (const beam of this.beams) drawBeam(buf, light, beam);
    drawOrdnance(buf, light, this.coils, this.bolts, this.meteors, this.t);
    for (const blaze of this.blazes) {
      const k = blaze.life / blaze.max;
      const flick = Math.sin(blaze.life * 31 + blaze.x) > 0;
      const color = k > 0.4 ? (flick ? YELLOW : ORANGE) : hex("#c9281f");
      buf.set(Math.floor(blaze.x / CELL), Math.floor(blaze.y / CELL), color);
      light.glow(blaze.x / CELL, blaze.y / CELL, 6 + 5 * k * blaze.power, ORANGE, 0.3 * k * blaze.power);
    }
    for (const flash of this.flashes) {
      const k = flash.life / flash.max;
      const x = flash.x / CELL;
      const y = flash.y / CELL;
      const r = flash.r * (0.8 + 0.2 * k);
      buf.disc(x, y, r + 1.2, DEEP);
      buf.disc(x, y, r, k > 0.45 ? HOT : YELLOW);
      buf.disc(x, y, r * 0.6, k > 0.45 ? WHITE : HOT);
    }
    for (const ring of this.rings) {
      const k = ring.life / ring.max;
      const grow = 1 - (1 - (1 - k)) ** 3;
      const radius = (ring.r0 + (ring.r1 - ring.r0) * grow) / CELL;
      buf.ring(ring.x / CELL, ring.y / CELL, radius, Math.max(1, ring.thick * k + 0.4), ring.color, Math.min(1, k * 1.6));
    }
    for (const bullet of this.bullets) {
      const speed = Math.hypot(bullet.vx, bullet.vy) || 1;
      const ux = bullet.vx / speed;
      const uy = bullet.vy / speed;
      const len = Math.min(bullet.dist, bullet.heavy ? 33 : bullet.weapon === "drone" ? 56 : 53);
      if (len < 1) continue;
      const hx = bullet.x / CELL;
      const hy = bullet.y / CELL;
      const tx = (bullet.x - ux * len) / CELL;
      const ty = (bullet.y - uy * len) / CELL;
      const mx = (bullet.x - ux * len * 0.5) / CELL;
      const my = (bullet.y - uy * len * 0.5) / CELL;
      buf.line(tx, ty, mx, my, ORANGE);
      buf.line(mx, my, hx, hy, YELLOW);
      buf.set(Math.round(hx), Math.round(hy), hex("#fffbe8"));
      light.glow(hx, hy, bullet.heavy ? 6 : 8, YELLOW, 0.45);
    }
    for (const glow of this.lights) {
      if (glow.color === undefined) continue;
      const o = glow.max > 0 ? glow.life / glow.max : 0;
      light.glow(glow.x / CELL, glow.y / CELL, (glow.r / CELL) * (0.55 + 0.45 * o), glow.color, glow.k * o * o);
    }
    for (const p of this.parts.list) {
      if (p.kind === "fireball" || p.kind === "chunk" || p.kind === "flame" || p.kind === "ember" || p.kind === "ion" || p.buf) {
        const kind = p.buf === "ion" || p.kind === "ion" ? "ion" : p.buf === "fire" ? "spark" : p.kind;
        paintCellPart(buf, light, {
          x: p.x, y: p.y, vx: p.vx, vy: p.vy, life: p.life, max: p.max, size: p.size, seed: p.seed ?? 0, kind,
        }, this.t);
      }
    }
  }

  private ensureFx(camX: number, camY: number): PixBuf {
    const cols = Math.ceil(this.viewW / CELL) + 4;
    const rows = Math.ceil(this.viewH / CELL) + 4;
    if (!this.fxBuf || this.fxBuf.w !== cols || this.fxBuf.h !== rows) {
      this.fxBuf = new PixBuf(cols, rows);
      const canvas = document.createElement("canvas");
      canvas.width = cols;
      canvas.height = rows;
      this.fxCanvas = canvas;
      this.fxImage = null;
    }
    const buf = this.fxBuf;
    buf.ox = Math.floor(camX / CELL) - 1;
    buf.oy = Math.floor(camY / CELL) - 1;
    buf.clear(0);
    return buf;
  }

  /** Watch block detach results. The world method returns the sprite; we poll dead blocks once. */
  pullChunks() {
    for (const block of this.world.blocks) {
      const flag = block as LiveFlag;
      if (flag.alive || flag.taken) continue;
      if (!block.canDetach) {
        flag.taken = true;
        continue;
      }
      flag.taken = true;
    }
  }
}

interface LiveFlag {
  alive: boolean;
  flew?: boolean;
  taken?: boolean;
  canDetach: boolean;
  damage: number;
  hp: number;
  lost: number;
  cells: number;
}

function clampCam(x: number, y: number, vw: number, vh: number, worldW: number, worldH: number, top: number): [number, number] {
  let cx = x;
  let cy = y;
  if (vw >= worldW) cx = (worldW - vw) / 2;
  else cx = Math.max(0, Math.min(worldW - vw, x));
  // A page that fits one screen stays flush with the canvas. The view is a few
  // pixels shorter than the extracted stage, and that must not open a sky band.
  if (vh + 48 >= worldH) cy = worldH >= vh ? 0 : worldH - vh;
  else if (vh >= worldH) cy = top < -40 ? Math.min(worldH - vh, Math.max(top, y)) : worldH - vh;
  else cy = Math.max(top, Math.min(worldH - vh, y));
  return [cx, cy];
}

export function patchDetach(_sim: Sim) {
  /* Block slabs are created by world.breaker inside Sim. */
}
